import { Branding, Configuration } from "./branding/branding";
import { ModalPublishConfiguration, ValidationFieldNamesConfiguration, ValidationSectionConfiguration, ValidationFieldNames } from "./branding/configurations/global/pageElements/ModalPublishConfiguration";
import { LanguageSwitchConfiguration } from "./branding/configurations/global/pageElements/LanguageSwitchConfiguration";
import { ActionbarConfiguration } from "./branding/configurations/global/pageElements/ActionBarConfiguration";
import { SidebarConfiguration, SidebarSection } from "./branding/configurations/global/pageElements/SideBarConfiguration";
import { EventCloudProductsConfiguration, EventCloudProduct } from "./branding/configurations/global/utilityConfigs/EventCloudProductsConfiguration";
import { QrCodesConfiguration } from "./branding/configurations/global/utilityConfigs/QrCodesConfiguration";
import { QrCodeFormat } from "./globalStates/ExhibitorDataTypes";
import { TopBarConfiguration, BuyMoreConfiguration } from "./branding/configurations/global/pageElements/TopBarConfiguration";
import { ProfilePageConfiguration, ProfileSection } from "./branding/configurations/content/profile/ProfilePageConfiguration";
import { UploadSectionConfiguration } from "./branding/configurations/content/profile/UploadSectionConfiguration";
import { VideoSectionConfiguration } from "./branding/configurations/content/profile/VideoSectionConfiguration";
import { BasicInfoSectionConfiguration, BasicInfoSectionConfigurationReadonly, BasicInfoSectionConfigurationMandatory } from "./branding/configurations/content/profile/BasicInfoSectionConfiguration";
import { DescriptionSectionConfiguration, DescriptionSectionConfigurationMandatory } from "./branding/configurations/content/profile/DescriptionSectionConfiguration";
import { KeywordsSectionConfiguration } from "./branding/configurations/content/profile/KeywordsSectionConfiguration";
import { SocialMediaSectionConfiguration, SocialMedia } from "./branding/configurations/content/profile/SocialMediaSectionConfiguration";
import { ConsentContentSectionConfiguration } from "./branding/configurations/content/profile/ConsentContentSectionConfiguration";
import { LinksPageConfiguration, LinkSectionConfiguration } from "./branding/configurations/content/LinksPageConfiguration";
import { CategoriesPageConfiguration } from "./branding/configurations/content/CategoriesPageConfiguration";
import { LicencesPageConfiguration } from "./branding/configurations/content/LicencesPageConfiguration";
import { ProductsPageConfiguration } from "./branding/configurations/content/products/ProductsPageConfiguration";
import { ProductSectionConfiguration } from "./branding/configurations/content/products/ProductSectionConfiguration";
import { MultimediaImageSectionConfiguration } from "./branding/configurations/content/multimedia/MultimediaImageSectionConfiguration";
import { ProductsForCollectionsPageConfiguration } from "./branding/configurations/content/products/ProductsForCollectionsPageConfiguration";
import { TrademarksPageConfiguration, TrademarkSectionConfiguration } from "./branding/configurations/content/TrademarksPageConfiguration";
import { TeamPageConfiguration } from "./branding/configurations/content/team/TeamPageConfiguration";
import { PersonSectionConfiguration } from "./branding/configurations/content/team/PersonSectionConfiguration";
import { MultimediaPageConfiguration } from "./branding/configurations/content/multimedia/MultimediaPageConfiguration";
import { ExhibitorNewsPageConfiguration } from "./branding/configurations/content/news/ExhibitorNewsPageConfiguration";
import { NewsSectionConfiguration } from "./branding/configurations/content/news/NewsSectionConfiguration";
import { OrganizerNewsPageConfiguration } from "./branding/configurations/content/news/OrganizerNewsPageConfiguration";
import { StoryNewsPageConfiguration } from "./branding/configurations/content/news/StoryNewsPageConfiguration";
import { EventDatesPageConfiguration } from "./branding/configurations/content/eventDates/EventDatesPageConfiguration";
import { EventDatesSectionConfiguration } from "./branding/configurations/content/eventDates/EventDatesSectionConfiguration";
import { NonCuratedEventDatesPageConfiguration } from "./branding/configurations/content/eventDates/NonCuratedEventDatesPageConfiguration";
import { ShowroomsPageConfiguration, ShowroomsSectionConfiguration } from "./branding/configurations/content/ShowroomsPageConfiguration";
import { CouponsPageConfiguration, CouponsSectionConfiguration } from "./branding/configurations/content/CouponsPageConfiguration";
import { JobsPageConfiguration, JobsSectionConfiguration } from "./branding/configurations/content/JobsPageConfiguration";
import { NotificationTemplatesPageConfiguration, NotificationsSectionConfiguration, NotificationTemplateDate, NotificationTemplateTime } from "./branding/configurations/content/NotificationTemplatesPageConfiguration";
import { PrintPageConfiguration, PrintSection } from "./branding/configurations/content/print/PrintPageConfiguration";
import { LogoBackgroundImageSectionConfiguration } from "./branding/configurations/content/print/LogoBackgroundImageSectionConfiguration";
import { CompanyNameSectionConfiguration } from "./branding/configurations/content/print/CompanyNameSectionConfiguration";
import { CompanySortSectionConfiguration } from "./branding/configurations/content/print/CompanySortSectionConfiguration";
import { AddressPrintCatalogueSectionConfiguration } from "./branding/configurations/content/print/AddressPrintCatalogueSectionConfiguration";
import { ContactInformationSectionConfiguration } from "./branding/configurations/content/print/ContactInformationSectionConfiguration";
import { CompanyDescriptionSectionConfiguration } from "./branding/configurations/content/print/CompanyDescriptionSectionConfiguration";
import { AdsSectionConfiguration } from "./branding/configurations/content/print/AdsSectionConfiguration";
import { ValidationMessagesConfiguration } from "./branding/configurations/global/validation/ValidationMessagesConfiguration";
import { ErrorMessagesConfiguration } from "./branding/configurations/global/validation/ErrorMessagesConfiguration";
import { CategoriesCountableConfiguration } from "./branding/configurations/global/utilityConfigs/CategoriesCountableConfiguration";
import { LocalizedString, ColorString } from "@corussoft/branding-generator";

export class BrandingData {
    protected lang: string;
    protected jsonData: any;
    protected subBrandings: BrandingData[] = [];

    constructor(jsonData: any, lang: string) {
        this.jsonData = jsonData
        this.lang = lang
    }

    setLang(lang: string) {
        this.lang = lang
        this.subBrandings.forEach(subBranding => subBranding.setLang(lang))
    }
}

export class BrandingImpl extends BrandingData implements Branding {
    _configuration!: Configuration;
    _pageTitle!: string;
    _availableLanguages!: string[];
    _contentLanguages!: string[];
    _showCounter!: boolean;
    _lblLimit!: LocalizedString;
    _rootCategoriesSelectable!: boolean;
    _useConfigDataV2!: boolean;
    _useUpselling!: boolean;
    _loaderBgColor!: string;
    _loaderSpinColor!: ColorString;

    get configuration() {
        if (!this._configuration) {
            this._configuration = new ConfigurationImpl(this.jsonData["configuration"], this.lang)
            this.subBrandings.push(this._configuration as any as BrandingData)
        }
        return this._configuration
    }

    get pageTitle() {
        return this.jsonData["pageTitle"]
    }

    get availableLanguages() {
        return this.jsonData["availableLanguages"]
    }

    get contentLanguages() {
        return this.jsonData["contentLanguages"]
    }

    get showCounter() {
        return this.jsonData["showCounter"]
    }

    get lblLimit() {
        return this.jsonData["lblLimit"][this.lang]
    }

    get rootCategoriesSelectable() {
        return this.jsonData["rootCategoriesSelectable"]
    }

    get useConfigDataV2() {
        return this.jsonData["useConfigDataV2"]
    }

    get useUpselling() {
        return this.jsonData["useUpselling"]
    }

    get loaderBgColor() {
        return this.jsonData["loaderBgColor"]
    }

    get loaderSpinColor() {
        return this.jsonData["loaderSpinColor"]
    }
}

class ConfigurationImpl extends BrandingData implements Configuration {
    _topicName!: string;
    _sotAccessToken!: string;
    _ogTitle!: LocalizedString;
    _ogDescription!: LocalizedString;
    _maincolor!: ColorString;
    _eventDays!: string[];
    _eventTimeZone!: string;
    _dateFormat!: string;
    _timeFormat!: string;
    _showCategoriesCode!: boolean;
    _showLicencesCode!: boolean;
    _hntSearch!: LocalizedString;
    _hntChips!: LocalizedString;
    _lblEmptySelect!: LocalizedString;
    _modalPublish!: ModalPublishConfiguration;
    _languageSwitch!: LanguageSwitchConfiguration;
    _actionbar!: ActionbarConfiguration;
    _sidebar!: SidebarConfiguration;
    _eventCloudProducts!: EventCloudProductsConfiguration;
    _qrCodes!: QrCodesConfiguration;
    _topBar!: TopBarConfiguration;
    _profilePage!: ProfilePageConfiguration;
    _linksPage!: LinksPageConfiguration;
    _categoriesPage!: CategoriesPageConfiguration;
    _licencesPage!: LicencesPageConfiguration;
    _productsPage!: ProductsPageConfiguration;
    _productsForCollectionsPage!: ProductsForCollectionsPageConfiguration;
    _trademarksPage!: TrademarksPageConfiguration;
    _teamPage!: TeamPageConfiguration;
    _multimediaPage!: MultimediaPageConfiguration;
    _newsPage!: ExhibitorNewsPageConfiguration;
    _organizerNewsPage!: OrganizerNewsPageConfiguration;
    _storyNewsPage!: StoryNewsPageConfiguration;
    _eventDatesPage!: EventDatesPageConfiguration;
    _nonCuratedEventDatesPage!: NonCuratedEventDatesPageConfiguration;
    _showroomsPage!: ShowroomsPageConfiguration;
    _couponsPage!: CouponsPageConfiguration;
    _jobsPage!: JobsPageConfiguration;
    _notificationTemplatesPage!: NotificationTemplatesPageConfiguration;
    _printPage!: PrintPageConfiguration;
    _validationMessages!: ValidationMessagesConfiguration;
    _errorMessages!: ErrorMessagesConfiguration;
    _countableCategories!: CategoriesCountableConfiguration;

    get topicName() {
        return this.jsonData["topicName"]
    }

    get sotAccessToken() {
        return this.jsonData["sotAccessToken"]
    }

    get ogTitle() {
        return this.jsonData["ogTitle"][this.lang]
    }

    get ogDescription() {
        return this.jsonData["ogDescription"][this.lang]
    }

    get maincolor() {
        return this.jsonData["maincolor"]
    }

    get eventDays() {
        return this.jsonData["eventDays"]
    }

    get eventTimeZone() {
        return this.jsonData["eventTimeZone"]
    }

    get dateFormat() {
        return this.jsonData["dateFormat"]
    }

    get timeFormat() {
        return this.jsonData["timeFormat"]
    }

    get showCategoriesCode() {
        return this.jsonData["showCategoriesCode"]
    }

    get showLicencesCode() {
        return this.jsonData["showLicencesCode"]
    }

    get hntSearch() {
        return this.jsonData["hntSearch"][this.lang]
    }

    get hntChips() {
        return this.jsonData["hntChips"][this.lang]
    }

    get lblEmptySelect() {
        return this.jsonData["lblEmptySelect"][this.lang]
    }

    get modalPublish() {
        if (!this._modalPublish) {
            this._modalPublish = new ModalPublishConfigurationImpl(this.jsonData["modalPublish"], this.lang)
            this.subBrandings.push(this._modalPublish as any as BrandingData)
        }
        return this._modalPublish
    }

    get languageSwitch() {
        if (!this._languageSwitch) {
            this._languageSwitch = new LanguageSwitchConfigurationImpl(this.jsonData["languageSwitch"], this.lang)
            this.subBrandings.push(this._languageSwitch as any as BrandingData)
        }
        return this._languageSwitch
    }

    get actionbar() {
        if (!this._actionbar) {
            this._actionbar = new ActionbarConfigurationImpl(this.jsonData["actionbar"], this.lang)
            this.subBrandings.push(this._actionbar as any as BrandingData)
        }
        return this._actionbar
    }

    get sidebar() {
        if (!this._sidebar) {
            this._sidebar = new SidebarConfigurationImpl(this.jsonData["sidebar"], this.lang)
            this.subBrandings.push(this._sidebar as any as BrandingData)
        }
        return this._sidebar
    }

    get eventCloudProducts() {
        if (!this._eventCloudProducts) {
            this._eventCloudProducts = new EventCloudProductsConfigurationImpl(this.jsonData["eventCloudProducts"], this.lang)
            this.subBrandings.push(this._eventCloudProducts as any as BrandingData)
        }
        return this._eventCloudProducts
    }

    get qrCodes() {
        if (!this._qrCodes) {
            this._qrCodes = new QrCodesConfigurationImpl(this.jsonData["qrCodes"], this.lang)
            this.subBrandings.push(this._qrCodes as any as BrandingData)
        }
        return this._qrCodes
    }

    get topBar() {
        if (!this._topBar) {
            this._topBar = new TopBarConfigurationImpl(this.jsonData["topBar"], this.lang)
            this.subBrandings.push(this._topBar as any as BrandingData)
        }
        return this._topBar
    }

    get profilePage() {
        if (!this._profilePage) {
            this._profilePage = new ProfilePageConfigurationImpl(this.jsonData["profilePage"], this.lang)
            this.subBrandings.push(this._profilePage as any as BrandingData)
        }
        return this._profilePage
    }

    get linksPage() {
        if (!this._linksPage) {
            this._linksPage = new LinksPageConfigurationImpl(this.jsonData["linksPage"], this.lang)
            this.subBrandings.push(this._linksPage as any as BrandingData)
        }
        return this._linksPage
    }

    get categoriesPage() {
        if (!this._categoriesPage) {
            this._categoriesPage = new CategoriesPageConfigurationImpl(this.jsonData["categoriesPage"], this.lang)
            this.subBrandings.push(this._categoriesPage as any as BrandingData)
        }
        return this._categoriesPage
    }

    get licencesPage() {
        if (!this._licencesPage) {
            this._licencesPage = new LicencesPageConfigurationImpl(this.jsonData["licencesPage"], this.lang)
            this.subBrandings.push(this._licencesPage as any as BrandingData)
        }
        return this._licencesPage
    }

    get productsPage() {
        if (!this._productsPage) {
            this._productsPage = new ProductsPageConfigurationImpl(this.jsonData["productsPage"], this.lang)
            this.subBrandings.push(this._productsPage as any as BrandingData)
        }
        return this._productsPage
    }

    get productsForCollectionsPage() {
        if (!this._productsForCollectionsPage) {
            this._productsForCollectionsPage = new ProductsForCollectionsPageConfigurationImpl(this.jsonData["productsForCollectionsPage"], this.lang)
            this.subBrandings.push(this._productsForCollectionsPage as any as BrandingData)
        }
        return this._productsForCollectionsPage
    }

    get trademarksPage() {
        if (!this._trademarksPage) {
            this._trademarksPage = new TrademarksPageConfigurationImpl(this.jsonData["trademarksPage"], this.lang)
            this.subBrandings.push(this._trademarksPage as any as BrandingData)
        }
        return this._trademarksPage
    }

    get teamPage() {
        if (!this._teamPage) {
            this._teamPage = new TeamPageConfigurationImpl(this.jsonData["teamPage"], this.lang)
            this.subBrandings.push(this._teamPage as any as BrandingData)
        }
        return this._teamPage
    }

    get multimediaPage() {
        if (!this._multimediaPage) {
            this._multimediaPage = new MultimediaPageConfigurationImpl(this.jsonData["multimediaPage"], this.lang)
            this.subBrandings.push(this._multimediaPage as any as BrandingData)
        }
        return this._multimediaPage
    }

    get newsPage() {
        if (!this._newsPage) {
            this._newsPage = new ExhibitorNewsPageConfigurationImpl(this.jsonData["newsPage"], this.lang)
            this.subBrandings.push(this._newsPage as any as BrandingData)
        }
        return this._newsPage
    }

    get organizerNewsPage() {
        if (!this._organizerNewsPage) {
            this._organizerNewsPage = new OrganizerNewsPageConfigurationImpl(this.jsonData["organizerNewsPage"], this.lang)
            this.subBrandings.push(this._organizerNewsPage as any as BrandingData)
        }
        return this._organizerNewsPage
    }

    get storyNewsPage() {
        if (!this._storyNewsPage) {
            this._storyNewsPage = new StoryNewsPageConfigurationImpl(this.jsonData["storyNewsPage"], this.lang)
            this.subBrandings.push(this._storyNewsPage as any as BrandingData)
        }
        return this._storyNewsPage
    }

    get eventDatesPage() {
        if (!this._eventDatesPage) {
            this._eventDatesPage = new EventDatesPageConfigurationImpl(this.jsonData["eventDatesPage"], this.lang)
            this.subBrandings.push(this._eventDatesPage as any as BrandingData)
        }
        return this._eventDatesPage
    }

    get nonCuratedEventDatesPage() {
        if (!this._nonCuratedEventDatesPage) {
            this._nonCuratedEventDatesPage = new NonCuratedEventDatesPageConfigurationImpl(this.jsonData["nonCuratedEventDatesPage"], this.lang)
            this.subBrandings.push(this._nonCuratedEventDatesPage as any as BrandingData)
        }
        return this._nonCuratedEventDatesPage
    }

    get showroomsPage() {
        if (!this._showroomsPage) {
            this._showroomsPage = new ShowroomsPageConfigurationImpl(this.jsonData["showroomsPage"], this.lang)
            this.subBrandings.push(this._showroomsPage as any as BrandingData)
        }
        return this._showroomsPage
    }

    get couponsPage() {
        if (!this._couponsPage) {
            this._couponsPage = new CouponsPageConfigurationImpl(this.jsonData["couponsPage"], this.lang)
            this.subBrandings.push(this._couponsPage as any as BrandingData)
        }
        return this._couponsPage
    }

    get jobsPage() {
        if (!this._jobsPage) {
            this._jobsPage = new JobsPageConfigurationImpl(this.jsonData["jobsPage"], this.lang)
            this.subBrandings.push(this._jobsPage as any as BrandingData)
        }
        return this._jobsPage
    }

    get notificationTemplatesPage() {
        if (!this._notificationTemplatesPage) {
            this._notificationTemplatesPage = new NotificationTemplatesPageConfigurationImpl(this.jsonData["notificationTemplatesPage"], this.lang)
            this.subBrandings.push(this._notificationTemplatesPage as any as BrandingData)
        }
        return this._notificationTemplatesPage
    }

    get printPage() {
        if (!this._printPage) {
            this._printPage = new PrintPageConfigurationImpl(this.jsonData["printPage"], this.lang)
            this.subBrandings.push(this._printPage as any as BrandingData)
        }
        return this._printPage
    }

    get validationMessages() {
        if (!this._validationMessages) {
            this._validationMessages = new ValidationMessagesConfigurationImpl(this.jsonData["validationMessages"], this.lang)
            this.subBrandings.push(this._validationMessages as any as BrandingData)
        }
        return this._validationMessages
    }

    get errorMessages() {
        if (!this._errorMessages) {
            this._errorMessages = new ErrorMessagesConfigurationImpl(this.jsonData["errorMessages"], this.lang)
            this.subBrandings.push(this._errorMessages as any as BrandingData)
        }
        return this._errorMessages
    }

    get countableCategories() {
        if (!this._countableCategories) {
            this._countableCategories = new CategoriesCountableConfigurationImpl(this.jsonData["countableCategories"], this.lang)
            this.subBrandings.push(this._countableCategories as any as BrandingData)
        }
        return this._countableCategories
    }
}

class ModalPublishConfigurationImpl extends BrandingData implements ModalPublishConfiguration {
    _title!: LocalizedString;
    _label!: LocalizedString;
    _fieldNamesConfig!: ValidationFieldNamesConfiguration;
    _visibilityDuration!: number;

    get title() {
        return this.jsonData["title"][this.lang]
    }

    get label() {
        return this.jsonData["label"][this.lang]
    }

    get fieldNamesConfig() {
        if (!this._fieldNamesConfig) {
            this._fieldNamesConfig = new ValidationFieldNamesConfigurationImpl(this.jsonData["fieldNamesConfig"], this.lang)
            this.subBrandings.push(this._fieldNamesConfig as any as BrandingData)
        }
        return this._fieldNamesConfig
    }

    get visibilityDuration() {
        return this.jsonData["visibilityDuration"]
    }
}

class ValidationFieldNamesConfigurationImpl extends BrandingData implements ValidationFieldNamesConfiguration {
    _organization!: ValidationSectionConfiguration;
    _links!: ValidationSectionConfiguration;
    _products!: ValidationSectionConfiguration;
    _productsForCollections!: ValidationSectionConfiguration;
    _trademarks!: ValidationSectionConfiguration;
    _persons!: ValidationSectionConfiguration;
    _medias!: ValidationSectionConfiguration;
    _exhibitorNewsList!: ValidationSectionConfiguration;
    _storyNewsList!: ValidationSectionConfiguration;
    _curatedEventDates!: ValidationSectionConfiguration;
    _nonCuratedEventDates!: ValidationSectionConfiguration;
    _showrooms!: ValidationSectionConfiguration;
    _goodies!: ValidationSectionConfiguration;
    _jobOffers!: ValidationSectionConfiguration;
    _printCatalog!: ValidationSectionConfiguration;
    _notificationTemplates!: ValidationSectionConfiguration;
    _notificationPromotions!: ValidationSectionConfiguration;

    get organization() {
        if (!this._organization) {
            this._organization = new ValidationSectionConfigurationImpl(this.jsonData["organization"], this.lang)
            this.subBrandings.push(this._organization as any as BrandingData)
        }
        return this._organization
    }

    get links() {
        if (!this._links) {
            this._links = new ValidationSectionConfigurationImpl(this.jsonData["links"], this.lang)
            this.subBrandings.push(this._links as any as BrandingData)
        }
        return this._links
    }

    get products() {
        if (!this._products) {
            this._products = new ValidationSectionConfigurationImpl(this.jsonData["products"], this.lang)
            this.subBrandings.push(this._products as any as BrandingData)
        }
        return this._products
    }

    get productsForCollections() {
        if (!this._productsForCollections) {
            this._productsForCollections = new ValidationSectionConfigurationImpl(this.jsonData["productsForCollections"], this.lang)
            this.subBrandings.push(this._productsForCollections as any as BrandingData)
        }
        return this._productsForCollections
    }

    get trademarks() {
        if (!this._trademarks) {
            this._trademarks = new ValidationSectionConfigurationImpl(this.jsonData["trademarks"], this.lang)
            this.subBrandings.push(this._trademarks as any as BrandingData)
        }
        return this._trademarks
    }

    get persons() {
        if (!this._persons) {
            this._persons = new ValidationSectionConfigurationImpl(this.jsonData["persons"], this.lang)
            this.subBrandings.push(this._persons as any as BrandingData)
        }
        return this._persons
    }

    get medias() {
        if (!this._medias) {
            this._medias = new ValidationSectionConfigurationImpl(this.jsonData["medias"], this.lang)
            this.subBrandings.push(this._medias as any as BrandingData)
        }
        return this._medias
    }

    get exhibitorNewsList() {
        if (!this._exhibitorNewsList) {
            this._exhibitorNewsList = new ValidationSectionConfigurationImpl(this.jsonData["exhibitorNewsList"], this.lang)
            this.subBrandings.push(this._exhibitorNewsList as any as BrandingData)
        }
        return this._exhibitorNewsList
    }

    get storyNewsList() {
        if (!this._storyNewsList) {
            this._storyNewsList = new ValidationSectionConfigurationImpl(this.jsonData["storyNewsList"], this.lang)
            this.subBrandings.push(this._storyNewsList as any as BrandingData)
        }
        return this._storyNewsList
    }

    get curatedEventDates() {
        if (!this._curatedEventDates) {
            this._curatedEventDates = new ValidationSectionConfigurationImpl(this.jsonData["curatedEventDates"], this.lang)
            this.subBrandings.push(this._curatedEventDates as any as BrandingData)
        }
        return this._curatedEventDates
    }

    get nonCuratedEventDates() {
        if (!this._nonCuratedEventDates) {
            this._nonCuratedEventDates = new ValidationSectionConfigurationImpl(this.jsonData["nonCuratedEventDates"], this.lang)
            this.subBrandings.push(this._nonCuratedEventDates as any as BrandingData)
        }
        return this._nonCuratedEventDates
    }

    get showrooms() {
        if (!this._showrooms) {
            this._showrooms = new ValidationSectionConfigurationImpl(this.jsonData["showrooms"], this.lang)
            this.subBrandings.push(this._showrooms as any as BrandingData)
        }
        return this._showrooms
    }

    get goodies() {
        if (!this._goodies) {
            this._goodies = new ValidationSectionConfigurationImpl(this.jsonData["goodies"], this.lang)
            this.subBrandings.push(this._goodies as any as BrandingData)
        }
        return this._goodies
    }

    get jobOffers() {
        if (!this._jobOffers) {
            this._jobOffers = new ValidationSectionConfigurationImpl(this.jsonData["jobOffers"], this.lang)
            this.subBrandings.push(this._jobOffers as any as BrandingData)
        }
        return this._jobOffers
    }

    get printCatalog() {
        if (!this._printCatalog) {
            this._printCatalog = new ValidationSectionConfigurationImpl(this.jsonData["printCatalog"], this.lang)
            this.subBrandings.push(this._printCatalog as any as BrandingData)
        }
        return this._printCatalog
    }

    get notificationTemplates() {
        if (!this._notificationTemplates) {
            this._notificationTemplates = new ValidationSectionConfigurationImpl(this.jsonData["notificationTemplates"], this.lang)
            this.subBrandings.push(this._notificationTemplates as any as BrandingData)
        }
        return this._notificationTemplates
    }

    get notificationPromotions() {
        if (!this._notificationPromotions) {
            this._notificationPromotions = new ValidationSectionConfigurationImpl(this.jsonData["notificationPromotions"], this.lang)
            this.subBrandings.push(this._notificationPromotions as any as BrandingData)
        }
        return this._notificationPromotions
    }
}

class ValidationSectionConfigurationImpl extends BrandingData implements ValidationSectionConfiguration {
    _name!: LocalizedString;
    _nameIndex!: LocalizedString;
    _fieldNames!: ValidationFieldNames[];

    get name() {
        return this.jsonData["name"][this.lang]
    }

    get nameIndex() {
        return this.jsonData["nameIndex"][this.lang]
    }

    get fieldNames() {
        if (!this._fieldNames) {
            this._fieldNames = []
            for (const arrayElement of this.jsonData["fieldNames"]) {
                const arrayObject = new ValidationFieldNamesImpl(arrayElement, this.lang)
                this.fieldNames.push(arrayObject)
                this.subBrandings.push(arrayObject as any as BrandingData)
            }
        }
        return this._fieldNames
    }
}

class ValidationFieldNamesImpl extends BrandingData implements ValidationFieldNames {
    _name!: string;
    _value!: LocalizedString;

    get name() {
        return this.jsonData["name"]
    }

    get value() {
        return this.jsonData["value"][this.lang]
    }
}

class LanguageSwitchConfigurationImpl extends BrandingData implements LanguageSwitchConfiguration {
    _lblEnOption!: LocalizedString;
    _lblDeOption!: LocalizedString;
    _lblStandardTemplate!: LocalizedString;
    _lblTranslationTemplate!: LocalizedString;

    get lblEnOption() {
        return this.jsonData["lblEnOption"][this.lang]
    }

    get lblDeOption() {
        return this.jsonData["lblDeOption"][this.lang]
    }

    get lblStandardTemplate() {
        return this.jsonData["lblStandardTemplate"][this.lang]
    }

    get lblTranslationTemplate() {
        return this.jsonData["lblTranslationTemplate"][this.lang]
    }
}

class ActionbarConfigurationImpl extends BrandingData implements ActionbarConfiguration {
    _btnPublish!: LocalizedString;
    _btnDiscard!: LocalizedString;
    _modalDiscardTitle!: LocalizedString;
    _modalDiscardText!: LocalizedString;
    _modalDiscardOK!: LocalizedString;
    _modalDiscardCancel!: LocalizedString;

    get btnPublish() {
        return this.jsonData["btnPublish"][this.lang]
    }

    get btnDiscard() {
        return this.jsonData["btnDiscard"][this.lang]
    }

    get modalDiscardTitle() {
        return this.jsonData["modalDiscardTitle"][this.lang]
    }

    get modalDiscardText() {
        return this.jsonData["modalDiscardText"][this.lang]
    }

    get modalDiscardOK() {
        return this.jsonData["modalDiscardOK"][this.lang]
    }

    get modalDiscardCancel() {
        return this.jsonData["modalDiscardCancel"][this.lang]
    }
}

class SidebarConfigurationImpl extends BrandingData implements SidebarConfiguration {
    _sectionsOrder!: SidebarSection[];
    _lblProfile!: LocalizedString;
    _lblCategories!: LocalizedString;
    _lblLicences!: LocalizedString;
    _urlLicences!: string;
    _lblLinks!: LocalizedString;
    _lblProducts!: LocalizedString;
    _lblProductsForCollections!: LocalizedString;
    _lblTrademarks!: LocalizedString;
    _lblTeam!: LocalizedString;
    _lblMultimedia!: LocalizedString;
    _lblExhibitorNews!: LocalizedString;
    _lblOrganizerNews!: LocalizedString;
    _lblStoryNews!: LocalizedString;
    _lblEventDate!: LocalizedString;
    _lblExhibitorEvents!: LocalizedString;
    _lblShowrooms!: LocalizedString;
    _lblCoupons!: LocalizedString;
    _lblJobs!: LocalizedString;
    _lblPrint!: LocalizedString;
    _lblNotificationTemplates!: LocalizedString;
    _lblCustomLink!: LocalizedString;
    _urlCustomLink!: LocalizedString;
    _lblEventShop!: LocalizedString;
    _urlEventShop!: LocalizedString;
    _showEventShop!: boolean;
    _lblEventGuide!: LocalizedString;
    _urlEventGuide!: string;
    _showEventGuide!: boolean;

    get sectionsOrder() {
        return this.jsonData["sectionsOrder"]
    }

    get lblProfile() {
        return this.jsonData["lblProfile"][this.lang]
    }

    get lblCategories() {
        return this.jsonData["lblCategories"][this.lang]
    }

    get lblLicences() {
        return this.jsonData["lblLicences"][this.lang]
    }

    get urlLicences() {
        return this.jsonData["urlLicences"]
    }

    get lblLinks() {
        return this.jsonData["lblLinks"][this.lang]
    }

    get lblProducts() {
        return this.jsonData["lblProducts"][this.lang]
    }

    get lblProductsForCollections() {
        return this.jsonData["lblProductsForCollections"][this.lang]
    }

    get lblTrademarks() {
        return this.jsonData["lblTrademarks"][this.lang]
    }

    get lblTeam() {
        return this.jsonData["lblTeam"][this.lang]
    }

    get lblMultimedia() {
        return this.jsonData["lblMultimedia"][this.lang]
    }

    get lblExhibitorNews() {
        return this.jsonData["lblExhibitorNews"][this.lang]
    }

    get lblOrganizerNews() {
        return this.jsonData["lblOrganizerNews"][this.lang]
    }

    get lblStoryNews() {
        return this.jsonData["lblStoryNews"][this.lang]
    }

    get lblEventDate() {
        return this.jsonData["lblEventDate"][this.lang]
    }

    get lblExhibitorEvents() {
        return this.jsonData["lblExhibitorEvents"][this.lang]
    }

    get lblShowrooms() {
        return this.jsonData["lblShowrooms"][this.lang]
    }

    get lblCoupons() {
        return this.jsonData["lblCoupons"][this.lang]
    }

    get lblJobs() {
        return this.jsonData["lblJobs"][this.lang]
    }

    get lblPrint() {
        return this.jsonData["lblPrint"][this.lang]
    }

    get lblNotificationTemplates() {
        return this.jsonData["lblNotificationTemplates"][this.lang]
    }

    get lblCustomLink() {
        return this.jsonData["lblCustomLink"][this.lang]
    }

    get urlCustomLink() {
        return this.jsonData["urlCustomLink"][this.lang]
    }

    get lblEventShop() {
        return this.jsonData["lblEventShop"][this.lang]
    }

    get urlEventShop() {
        return this.jsonData["urlEventShop"][this.lang]
    }

    get showEventShop() {
        return this.jsonData["showEventShop"]
    }

    get lblEventGuide() {
        return this.jsonData["lblEventGuide"][this.lang]
    }

    get urlEventGuide() {
        return this.jsonData["urlEventGuide"]
    }

    get showEventGuide() {
        return this.jsonData["showEventGuide"]
    }
}

class EventCloudProductsConfigurationImpl extends BrandingData implements EventCloudProductsConfiguration {
    _list!: EventCloudProduct[];
    _nameEventGuide!: string;
    _nameMobileGuide!: string;
    _nameWebWidgets!: string;
    _titleSwitcher!: LocalizedString;

    get list() {
        return this.jsonData["list"]
    }

    get nameEventGuide() {
        return this.jsonData["nameEventGuide"]
    }

    get nameMobileGuide() {
        return this.jsonData["nameMobileGuide"]
    }

    get nameWebWidgets() {
        return this.jsonData["nameWebWidgets"]
    }

    get titleSwitcher() {
        return this.jsonData["titleSwitcher"][this.lang]
    }
}

class QrCodesConfigurationImpl extends BrandingData implements QrCodesConfiguration {
    _enabled!: boolean;
    _format!: QrCodeFormat;
    _modalTitle!: LocalizedString;
    _downloadButtonText!: LocalizedString;
    _cancelButtonText!: LocalizedString;
    _errorTitle!: LocalizedString;
    _errorMessage!: LocalizedString;

    get enabled() {
        return this.jsonData["enabled"]
    }

    get format() {
        return this.jsonData["format"]
    }

    get modalTitle() {
        return this.jsonData["modalTitle"][this.lang]
    }

    get downloadButtonText() {
        return this.jsonData["downloadButtonText"][this.lang]
    }

    get cancelButtonText() {
        return this.jsonData["cancelButtonText"][this.lang]
    }

    get errorTitle() {
        return this.jsonData["errorTitle"][this.lang]
    }

    get errorMessage() {
        return this.jsonData["errorMessage"][this.lang]
    }
}

class TopBarConfigurationImpl extends BrandingData implements TopBarConfiguration {
    _buyMoreConfigs!: BuyMoreConfiguration[];
    _buyMoreButtonText!: LocalizedString;
    _downloadQrCodeButtonText!: LocalizedString;

    get buyMoreConfigs() {
        if (!this._buyMoreConfigs) {
            this._buyMoreConfigs = []
            for (const arrayElement of this.jsonData["buyMoreConfigs"]) {
                const arrayObject = new BuyMoreConfigurationImpl(arrayElement, this.lang)
                this.buyMoreConfigs.push(arrayObject)
                this.subBrandings.push(arrayObject as any as BrandingData)
            }
        }
        return this._buyMoreConfigs
    }

    get buyMoreButtonText() {
        return this.jsonData["buyMoreButtonText"][this.lang]
    }

    get downloadQrCodeButtonText() {
        return this.jsonData["downloadQrCodeButtonText"][this.lang]
    }
}

class BuyMoreConfigurationImpl extends BrandingData implements BuyMoreConfiguration {
    _page!: SidebarSection;
    _link!: LocalizedString;

    get page() {
        return this.jsonData["page"]
    }

    get link() {
        return this.jsonData["link"][this.lang]
    }
}

class ProfilePageConfigurationImpl extends BrandingData implements ProfilePageConfiguration {
    _pageDescription!: LocalizedString;
    _sectionsOrder!: ProfileSection[];
    _sctUpload!: UploadSectionConfiguration;
    _sctVideo!: VideoSectionConfiguration;
    _sctBasicInfo!: BasicInfoSectionConfiguration;
    _sctBasicInfoReadonly!: BasicInfoSectionConfigurationReadonly;
    _sctBasicInfoMandatory!: BasicInfoSectionConfigurationMandatory;
    _sctDescription!: DescriptionSectionConfiguration;
    _sctDescriptionMandatory!: DescriptionSectionConfigurationMandatory;
    _sctKeywords!: KeywordsSectionConfiguration;
    _sctSocialMedia!: SocialMediaSectionConfiguration;
    _sctConsentContent!: ConsentContentSectionConfiguration;

    get pageDescription() {
        return this.jsonData["pageDescription"][this.lang]
    }

    get sectionsOrder() {
        return this.jsonData["sectionsOrder"]
    }

    get sctUpload() {
        if (!this._sctUpload) {
            this._sctUpload = new UploadSectionConfigurationImpl(this.jsonData["sctUpload"], this.lang)
            this.subBrandings.push(this._sctUpload as any as BrandingData)
        }
        return this._sctUpload
    }

    get sctVideo() {
        if (!this._sctVideo) {
            this._sctVideo = new VideoSectionConfigurationImpl(this.jsonData["sctVideo"], this.lang)
            this.subBrandings.push(this._sctVideo as any as BrandingData)
        }
        return this._sctVideo
    }

    get sctBasicInfo() {
        if (!this._sctBasicInfo) {
            this._sctBasicInfo = new BasicInfoSectionConfigurationImpl(this.jsonData["sctBasicInfo"], this.lang)
            this.subBrandings.push(this._sctBasicInfo as any as BrandingData)
        }
        return this._sctBasicInfo
    }

    get sctBasicInfoReadonly() {
        if (!this._sctBasicInfoReadonly) {
            this._sctBasicInfoReadonly = new BasicInfoSectionConfigurationReadonlyImpl(this.jsonData["sctBasicInfoReadonly"], this.lang)
            this.subBrandings.push(this._sctBasicInfoReadonly as any as BrandingData)
        }
        return this._sctBasicInfoReadonly
    }

    get sctBasicInfoMandatory() {
        if (!this._sctBasicInfoMandatory) {
            this._sctBasicInfoMandatory = new BasicInfoSectionConfigurationMandatoryImpl(this.jsonData["sctBasicInfoMandatory"], this.lang)
            this.subBrandings.push(this._sctBasicInfoMandatory as any as BrandingData)
        }
        return this._sctBasicInfoMandatory
    }

    get sctDescription() {
        if (!this._sctDescription) {
            this._sctDescription = new DescriptionSectionConfigurationImpl(this.jsonData["sctDescription"], this.lang)
            this.subBrandings.push(this._sctDescription as any as BrandingData)
        }
        return this._sctDescription
    }

    get sctDescriptionMandatory() {
        if (!this._sctDescriptionMandatory) {
            this._sctDescriptionMandatory = new DescriptionSectionConfigurationMandatoryImpl(this.jsonData["sctDescriptionMandatory"], this.lang)
            this.subBrandings.push(this._sctDescriptionMandatory as any as BrandingData)
        }
        return this._sctDescriptionMandatory
    }

    get sctKeywords() {
        if (!this._sctKeywords) {
            this._sctKeywords = new KeywordsSectionConfigurationImpl(this.jsonData["sctKeywords"], this.lang)
            this.subBrandings.push(this._sctKeywords as any as BrandingData)
        }
        return this._sctKeywords
    }

    get sctSocialMedia() {
        if (!this._sctSocialMedia) {
            this._sctSocialMedia = new SocialMediaSectionConfigurationImpl(this.jsonData["sctSocialMedia"], this.lang)
            this.subBrandings.push(this._sctSocialMedia as any as BrandingData)
        }
        return this._sctSocialMedia
    }

    get sctConsentContent() {
        if (!this._sctConsentContent) {
            this._sctConsentContent = new ConsentContentSectionConfigurationImpl(this.jsonData["sctConsentContent"], this.lang)
            this.subBrandings.push(this._sctConsentContent as any as BrandingData)
        }
        return this._sctConsentContent
    }
}

class UploadSectionConfigurationImpl extends BrandingData implements UploadSectionConfiguration {
    _lblUpload!: LocalizedString;
    _lblCompanyLogo!: LocalizedString;
    _hntTitleCompanyLogo!: LocalizedString;
    _hntPropCompanyLogo!: LocalizedString;
    _btnBrowseFilesLogo!: LocalizedString;
    _btnDeleteImgLogo!: LocalizedString;
    _btnBuyLogoLabel!: LocalizedString;
    _btnBuyLogoLink!: LocalizedString;
    _lblCompanyBackgroundImage!: LocalizedString;
    _hntTitleDesktopBackgroundImage!: LocalizedString;
    _hntPropDesktopBackgroundImage!: LocalizedString;
    _hntTitleMobileBackgroundImage!: LocalizedString;
    _hntPropMobileBackgroundImage!: LocalizedString;
    _btnBrowseFilesBackgroundImage!: LocalizedString;
    _btnDeleteImgBackgroundImage!: LocalizedString;
    _btnBuyHeaderGraphicLabel!: LocalizedString;
    _btnBuyHeaderGraphicLink!: LocalizedString;

    get lblUpload() {
        return this.jsonData["lblUpload"][this.lang]
    }

    get lblCompanyLogo() {
        return this.jsonData["lblCompanyLogo"][this.lang]
    }

    get hntTitleCompanyLogo() {
        return this.jsonData["hntTitleCompanyLogo"][this.lang]
    }

    get hntPropCompanyLogo() {
        return this.jsonData["hntPropCompanyLogo"][this.lang]
    }

    get btnBrowseFilesLogo() {
        return this.jsonData["btnBrowseFilesLogo"][this.lang]
    }

    get btnDeleteImgLogo() {
        return this.jsonData["btnDeleteImgLogo"][this.lang]
    }

    get btnBuyLogoLabel() {
        return this.jsonData["btnBuyLogoLabel"][this.lang]
    }

    get btnBuyLogoLink() {
        return this.jsonData["btnBuyLogoLink"][this.lang]
    }

    get lblCompanyBackgroundImage() {
        return this.jsonData["lblCompanyBackgroundImage"][this.lang]
    }

    get hntTitleDesktopBackgroundImage() {
        return this.jsonData["hntTitleDesktopBackgroundImage"][this.lang]
    }

    get hntPropDesktopBackgroundImage() {
        return this.jsonData["hntPropDesktopBackgroundImage"][this.lang]
    }

    get hntTitleMobileBackgroundImage() {
        return this.jsonData["hntTitleMobileBackgroundImage"][this.lang]
    }

    get hntPropMobileBackgroundImage() {
        return this.jsonData["hntPropMobileBackgroundImage"][this.lang]
    }

    get btnBrowseFilesBackgroundImage() {
        return this.jsonData["btnBrowseFilesBackgroundImage"][this.lang]
    }

    get btnDeleteImgBackgroundImage() {
        return this.jsonData["btnDeleteImgBackgroundImage"][this.lang]
    }

    get btnBuyHeaderGraphicLabel() {
        return this.jsonData["btnBuyHeaderGraphicLabel"][this.lang]
    }

    get btnBuyHeaderGraphicLink() {
        return this.jsonData["btnBuyHeaderGraphicLink"][this.lang]
    }
}

class VideoSectionConfigurationImpl extends BrandingData implements VideoSectionConfiguration {
    _lblCompanyVideo!: LocalizedString;
    _lblVideo!: LocalizedString;
    _hntTitleVideo!: LocalizedString;
    _hntPropVideo!: LocalizedString;
    _btnDeleteVideo!: LocalizedString;
    _lblThumbnail!: LocalizedString;
    _hntTitleThumbnail!: LocalizedString;
    _hntPropThumbnail!: LocalizedString;
    _btnDeleteImg!: LocalizedString;
    _btnBrowseFiles!: LocalizedString;

    get lblCompanyVideo() {
        return this.jsonData["lblCompanyVideo"][this.lang]
    }

    get lblVideo() {
        return this.jsonData["lblVideo"][this.lang]
    }

    get hntTitleVideo() {
        return this.jsonData["hntTitleVideo"][this.lang]
    }

    get hntPropVideo() {
        return this.jsonData["hntPropVideo"][this.lang]
    }

    get btnDeleteVideo() {
        return this.jsonData["btnDeleteVideo"][this.lang]
    }

    get lblThumbnail() {
        return this.jsonData["lblThumbnail"][this.lang]
    }

    get hntTitleThumbnail() {
        return this.jsonData["hntTitleThumbnail"][this.lang]
    }

    get hntPropThumbnail() {
        return this.jsonData["hntPropThumbnail"][this.lang]
    }

    get btnDeleteImg() {
        return this.jsonData["btnDeleteImg"][this.lang]
    }

    get btnBrowseFiles() {
        return this.jsonData["btnBrowseFiles"][this.lang]
    }
}

class BasicInfoSectionConfigurationImpl extends BrandingData implements BasicInfoSectionConfiguration {
    _lblBasicInformation!: LocalizedString;
    _lblCompany!: LocalizedString;
    _hntCompany!: LocalizedString;
    _lblIndustry!: LocalizedString;
    _hntIndustry!: LocalizedString;
    _lblPhoneNumber!: LocalizedString;
    _hntPhoneNumber!: LocalizedString;
    _lblMobile!: LocalizedString;
    _hntMobile!: LocalizedString;
    _lblFax!: LocalizedString;
    _hntFax!: LocalizedString;
    _lblEmail!: LocalizedString;
    _hntEmail!: LocalizedString;
    _lblWebsite!: LocalizedString;
    _hntWebsite!: LocalizedString;
    _lblAddress1!: LocalizedString;
    _hntAddress1!: LocalizedString;
    _lblAddress2!: LocalizedString;
    _hntAddress2!: LocalizedString;
    _lblAddress3!: LocalizedString;
    _hntAddress3!: LocalizedString;
    _lblZipCode!: LocalizedString;
    _hntZipCode!: LocalizedString;
    _lblCity!: LocalizedString;
    _hntCity!: LocalizedString;
    _lblCountry!: LocalizedString;
    _hntCountry!: LocalizedString;
    _lblCompanySize!: LocalizedString;
    _hntCompanySize!: LocalizedString;
    _lblFoundingYear!: LocalizedString;
    _hntFoundingYear!: LocalizedString;

    get lblBasicInformation() {
        return this.jsonData["lblBasicInformation"][this.lang]
    }

    get lblCompany() {
        return this.jsonData["lblCompany"][this.lang]
    }

    get hntCompany() {
        return this.jsonData["hntCompany"][this.lang]
    }

    get lblIndustry() {
        return this.jsonData["lblIndustry"][this.lang]
    }

    get hntIndustry() {
        return this.jsonData["hntIndustry"][this.lang]
    }

    get lblPhoneNumber() {
        return this.jsonData["lblPhoneNumber"][this.lang]
    }

    get hntPhoneNumber() {
        return this.jsonData["hntPhoneNumber"][this.lang]
    }

    get lblMobile() {
        return this.jsonData["lblMobile"][this.lang]
    }

    get hntMobile() {
        return this.jsonData["hntMobile"][this.lang]
    }

    get lblFax() {
        return this.jsonData["lblFax"][this.lang]
    }

    get hntFax() {
        return this.jsonData["hntFax"][this.lang]
    }

    get lblEmail() {
        return this.jsonData["lblEmail"][this.lang]
    }

    get hntEmail() {
        return this.jsonData["hntEmail"][this.lang]
    }

    get lblWebsite() {
        return this.jsonData["lblWebsite"][this.lang]
    }

    get hntWebsite() {
        return this.jsonData["hntWebsite"][this.lang]
    }

    get lblAddress1() {
        return this.jsonData["lblAddress1"][this.lang]
    }

    get hntAddress1() {
        return this.jsonData["hntAddress1"][this.lang]
    }

    get lblAddress2() {
        return this.jsonData["lblAddress2"][this.lang]
    }

    get hntAddress2() {
        return this.jsonData["hntAddress2"][this.lang]
    }

    get lblAddress3() {
        return this.jsonData["lblAddress3"][this.lang]
    }

    get hntAddress3() {
        return this.jsonData["hntAddress3"][this.lang]
    }

    get lblZipCode() {
        return this.jsonData["lblZipCode"][this.lang]
    }

    get hntZipCode() {
        return this.jsonData["hntZipCode"][this.lang]
    }

    get lblCity() {
        return this.jsonData["lblCity"][this.lang]
    }

    get hntCity() {
        return this.jsonData["hntCity"][this.lang]
    }

    get lblCountry() {
        return this.jsonData["lblCountry"][this.lang]
    }

    get hntCountry() {
        return this.jsonData["hntCountry"][this.lang]
    }

    get lblCompanySize() {
        return this.jsonData["lblCompanySize"][this.lang]
    }

    get hntCompanySize() {
        return this.jsonData["hntCompanySize"][this.lang]
    }

    get lblFoundingYear() {
        return this.jsonData["lblFoundingYear"][this.lang]
    }

    get hntFoundingYear() {
        return this.jsonData["hntFoundingYear"][this.lang]
    }
}

class BasicInfoSectionConfigurationReadonlyImpl extends BrandingData implements BasicInfoSectionConfigurationReadonly {
    _companyNameReadonly!: boolean;
    _phoneNumberReadonly!: boolean;
    _mobileNumberReadonly!: boolean;
    _faxReadonly!: boolean;
    _emailReadonly!: boolean;
    _websiteReadonly!: boolean;
    _address1Readonly!: boolean;
    _address2Readonly!: boolean;
    _address3Readonly!: boolean;
    _zipCodeReadonly!: boolean;
    _cityReadonly!: boolean;
    _countryReadonly!: boolean;
    _companySizeReadonly!: boolean;
    _industryReadonly!: boolean;
    _foundingYearReadonly!: boolean;

    get companyNameReadonly() {
        return this.jsonData["companyNameReadonly"]
    }

    get phoneNumberReadonly() {
        return this.jsonData["phoneNumberReadonly"]
    }

    get mobileNumberReadonly() {
        return this.jsonData["mobileNumberReadonly"]
    }

    get faxReadonly() {
        return this.jsonData["faxReadonly"]
    }

    get emailReadonly() {
        return this.jsonData["emailReadonly"]
    }

    get websiteReadonly() {
        return this.jsonData["websiteReadonly"]
    }

    get address1Readonly() {
        return this.jsonData["address1Readonly"]
    }

    get address2Readonly() {
        return this.jsonData["address2Readonly"]
    }

    get address3Readonly() {
        return this.jsonData["address3Readonly"]
    }

    get zipCodeReadonly() {
        return this.jsonData["zipCodeReadonly"]
    }

    get cityReadonly() {
        return this.jsonData["cityReadonly"]
    }

    get countryReadonly() {
        return this.jsonData["countryReadonly"]
    }

    get companySizeReadonly() {
        return this.jsonData["companySizeReadonly"]
    }

    get industryReadonly() {
        return this.jsonData["industryReadonly"]
    }

    get foundingYearReadonly() {
        return this.jsonData["foundingYearReadonly"]
    }
}

class BasicInfoSectionConfigurationMandatoryImpl extends BrandingData implements BasicInfoSectionConfigurationMandatory {
    _companyNameMandatory!: boolean;
    _phoneNumberMandatory!: boolean;
    _mobileNumberMandatory!: boolean;
    _faxMandatory!: boolean;
    _emailMandatory!: boolean;
    _websiteMandatory!: boolean;
    _address1Mandatory!: boolean;
    _address2Mandatory!: boolean;
    _address3Mandatory!: boolean;
    _zipCodeMandatory!: boolean;
    _cityMandatory!: boolean;
    _countryMandatory!: boolean;
    _companySizeMandatory!: boolean;
    _industryMandatory!: boolean;
    _foundingYearMandatory!: boolean;

    get companyNameMandatory() {
        return this.jsonData["companyNameMandatory"]
    }

    get phoneNumberMandatory() {
        return this.jsonData["phoneNumberMandatory"]
    }

    get mobileNumberMandatory() {
        return this.jsonData["mobileNumberMandatory"]
    }

    get faxMandatory() {
        return this.jsonData["faxMandatory"]
    }

    get emailMandatory() {
        return this.jsonData["emailMandatory"]
    }

    get websiteMandatory() {
        return this.jsonData["websiteMandatory"]
    }

    get address1Mandatory() {
        return this.jsonData["address1Mandatory"]
    }

    get address2Mandatory() {
        return this.jsonData["address2Mandatory"]
    }

    get address3Mandatory() {
        return this.jsonData["address3Mandatory"]
    }

    get zipCodeMandatory() {
        return this.jsonData["zipCodeMandatory"]
    }

    get cityMandatory() {
        return this.jsonData["cityMandatory"]
    }

    get countryMandatory() {
        return this.jsonData["countryMandatory"]
    }

    get companySizeMandatory() {
        return this.jsonData["companySizeMandatory"]
    }

    get industryMandatory() {
        return this.jsonData["industryMandatory"]
    }

    get foundingYearMandatory() {
        return this.jsonData["foundingYearMandatory"]
    }
}

class DescriptionSectionConfigurationImpl extends BrandingData implements DescriptionSectionConfiguration {
    _lblDescription!: LocalizedString;
    _lblLongDescription!: LocalizedString;
    _hntLongDescription!: LocalizedString;
    _lblShortDescription!: LocalizedString;
    _hntShortDescription!: LocalizedString;

    get lblDescription() {
        return this.jsonData["lblDescription"][this.lang]
    }

    get lblLongDescription() {
        return this.jsonData["lblLongDescription"][this.lang]
    }

    get hntLongDescription() {
        return this.jsonData["hntLongDescription"][this.lang]
    }

    get lblShortDescription() {
        return this.jsonData["lblShortDescription"][this.lang]
    }

    get hntShortDescription() {
        return this.jsonData["hntShortDescription"][this.lang]
    }
}

class DescriptionSectionConfigurationMandatoryImpl extends BrandingData implements DescriptionSectionConfigurationMandatory {
    _companyLongDescriptionMandatory!: boolean;
    _companyShortDescriptionMandatory!: boolean;

    get companyLongDescriptionMandatory() {
        return this.jsonData["companyLongDescriptionMandatory"]
    }

    get companyShortDescriptionMandatory() {
        return this.jsonData["companyShortDescriptionMandatory"]
    }
}

class KeywordsSectionConfigurationImpl extends BrandingData implements KeywordsSectionConfiguration {
    _title!: LocalizedString;
    _lblKeywords!: LocalizedString;
    _hntKeywords!: LocalizedString;
    _btnBuyMoreDisplay!: boolean;
    _btnBuyMoreLink!: LocalizedString;

    get title() {
        return this.jsonData["title"][this.lang]
    }

    get lblKeywords() {
        return this.jsonData["lblKeywords"][this.lang]
    }

    get hntKeywords() {
        return this.jsonData["hntKeywords"][this.lang]
    }

    get btnBuyMoreDisplay() {
        return this.jsonData["btnBuyMoreDisplay"]
    }

    get btnBuyMoreLink() {
        return this.jsonData["btnBuyMoreLink"][this.lang]
    }
}

class SocialMediaSectionConfigurationImpl extends BrandingData implements SocialMediaSectionConfiguration {
    _lblSocialMedia!: LocalizedString;
    _socialMediasOrder!: SocialMedia[];
    _lblFacebook!: LocalizedString;
    _hntFacebook!: LocalizedString;
    _lblInstagram!: LocalizedString;
    _hntInstagram!: LocalizedString;
    _lblLinkedIn!: LocalizedString;
    _hntLinkedIn!: LocalizedString;
    _lblXing!: LocalizedString;
    _hntXing!: LocalizedString;
    _lblX!: LocalizedString;
    _hntX!: LocalizedString;
    _lblYouTube!: LocalizedString;
    _hntYouTube!: LocalizedString;
    _lblPinterest!: LocalizedString;
    _hntPinterest!: LocalizedString;
    _lblTikTok!: LocalizedString;
    _hntTikTok!: LocalizedString;

    get lblSocialMedia() {
        return this.jsonData["lblSocialMedia"][this.lang]
    }

    get socialMediasOrder() {
        return this.jsonData["socialMediasOrder"]
    }

    get lblFacebook() {
        return this.jsonData["lblFacebook"][this.lang]
    }

    get hntFacebook() {
        return this.jsonData["hntFacebook"][this.lang]
    }

    get lblInstagram() {
        return this.jsonData["lblInstagram"][this.lang]
    }

    get hntInstagram() {
        return this.jsonData["hntInstagram"][this.lang]
    }

    get lblLinkedIn() {
        return this.jsonData["lblLinkedIn"][this.lang]
    }

    get hntLinkedIn() {
        return this.jsonData["hntLinkedIn"][this.lang]
    }

    get lblXing() {
        return this.jsonData["lblXing"][this.lang]
    }

    get hntXing() {
        return this.jsonData["hntXing"][this.lang]
    }

    get lblX() {
        return this.jsonData["lblX"][this.lang]
    }

    get hntX() {
        return this.jsonData["hntX"][this.lang]
    }

    get lblYouTube() {
        return this.jsonData["lblYouTube"][this.lang]
    }

    get hntYouTube() {
        return this.jsonData["hntYouTube"][this.lang]
    }

    get lblPinterest() {
        return this.jsonData["lblPinterest"][this.lang]
    }

    get hntPinterest() {
        return this.jsonData["hntPinterest"][this.lang]
    }

    get lblTikTok() {
        return this.jsonData["lblTikTok"][this.lang]
    }

    get hntTikTok() {
        return this.jsonData["hntTikTok"][this.lang]
    }
}

class ConsentContentSectionConfigurationImpl extends BrandingData implements ConsentContentSectionConfiguration {
    _lblConsentContent!: LocalizedString;
    _lblConsentTitle!: LocalizedString;
    _hntConsentTitle!: LocalizedString;
    _lblConsentText!: LocalizedString;
    _hntConsentText!: LocalizedString;

    get lblConsentContent() {
        return this.jsonData["lblConsentContent"][this.lang]
    }

    get lblConsentTitle() {
        return this.jsonData["lblConsentTitle"][this.lang]
    }

    get hntConsentTitle() {
        return this.jsonData["hntConsentTitle"][this.lang]
    }

    get lblConsentText() {
        return this.jsonData["lblConsentText"][this.lang]
    }

    get hntConsentText() {
        return this.jsonData["hntConsentText"][this.lang]
    }
}

class LinksPageConfigurationImpl extends BrandingData implements LinksPageConfiguration {
    _pageDescription!: LocalizedString;
    _btnAddLink!: LocalizedString;
    _sctLink!: LinkSectionConfiguration;

    get pageDescription() {
        return this.jsonData["pageDescription"][this.lang]
    }

    get btnAddLink() {
        return this.jsonData["btnAddLink"][this.lang]
    }

    get sctLink() {
        if (!this._sctLink) {
            this._sctLink = new LinkSectionConfigurationImpl(this.jsonData["sctLink"], this.lang)
            this.subBrandings.push(this._sctLink as any as BrandingData)
        }
        return this._sctLink
    }
}

class LinkSectionConfigurationImpl extends BrandingData implements LinkSectionConfiguration {
    _titleEmptyLink!: LocalizedString;
    _lblURL!: LocalizedString;
    _hntURL!: LocalizedString;
    _lblText!: LocalizedString;
    _hntText!: LocalizedString;

    get titleEmptyLink() {
        return this.jsonData["titleEmptyLink"][this.lang]
    }

    get lblURL() {
        return this.jsonData["lblURL"][this.lang]
    }

    get hntURL() {
        return this.jsonData["hntURL"][this.lang]
    }

    get lblText() {
        return this.jsonData["lblText"][this.lang]
    }

    get hntText() {
        return this.jsonData["hntText"][this.lang]
    }
}

class CategoriesPageConfigurationImpl extends BrandingData implements CategoriesPageConfiguration {
    _pageDescription!: LocalizedString;
    _categoryWhitelist!: string[];
    _expandedRootCategories!: string[];

    get pageDescription() {
        return this.jsonData["pageDescription"][this.lang]
    }

    get categoryWhitelist() {
        return this.jsonData["categoryWhitelist"]
    }

    get expandedRootCategories() {
        return this.jsonData["expandedRootCategories"]
    }
}

class LicencesPageConfigurationImpl extends BrandingData implements LicencesPageConfiguration {
    _pageDescription!: LocalizedString;
    _categoryWhitelist!: string[];

    get pageDescription() {
        return this.jsonData["pageDescription"][this.lang]
    }

    get categoryWhitelist() {
        return this.jsonData["categoryWhitelist"]
    }
}

class ProductsPageConfigurationImpl extends BrandingData implements ProductsPageConfiguration {
    _pageDescription!: LocalizedString;
    _btnAddProduct!: LocalizedString;
    _sctProduct!: ProductSectionConfiguration;

    get pageDescription() {
        return this.jsonData["pageDescription"][this.lang]
    }

    get btnAddProduct() {
        return this.jsonData["btnAddProduct"][this.lang]
    }

    get sctProduct() {
        if (!this._sctProduct) {
            this._sctProduct = new ProductSectionConfigurationImpl(this.jsonData["sctProduct"], this.lang)
            this.subBrandings.push(this._sctProduct as any as BrandingData)
        }
        return this._sctProduct
    }
}

class ProductSectionConfigurationImpl extends BrandingData implements ProductSectionConfiguration {
    _titleEmptyProduct!: LocalizedString;
    _lblProductImage!: LocalizedString;
    _lblProductImageMessage!: LocalizedString;
    _hntTitle!: LocalizedString;
    _hntPropImg!: LocalizedString;
    _btnBrowseFiles!: LocalizedString;
    _btnDeleteImg!: LocalizedString;
    _lblProductBackground!: LocalizedString;
    _hntTitleBackground!: LocalizedString;
    _hntPropBackground!: LocalizedString;
    _btnBrowseFilesBackground!: LocalizedString;
    _btnDeleteImgBackground!: LocalizedString;
    _lblProductname!: LocalizedString;
    _hntProductname!: LocalizedString;
    _lblProductPublicationStart!: LocalizedString;
    _hntProductPublicationStart!: LocalizedString;
    _lblProductPublicationEnd!: LocalizedString;
    _hntProductPublicationEnd!: LocalizedString;
    _lblDescriptionDetail!: LocalizedString;
    _hntDescriptionDetail!: LocalizedString;
    _lblDescriptionShort!: LocalizedString;
    _hntDescriptionShort!: LocalizedString;
    _lblTabCategories!: LocalizedString;
    _lblCategoriesDescription!: LocalizedString;
    _categoryWhitelist!: string[];
    _lblTabLinks!: LocalizedString;
    _lblLinksDescription!: LocalizedString;
    _lblTabMultimedia!: LocalizedString;
    _lblMultimediaDescription!: LocalizedString;
    _multimediaSection!: MultimediaImageSectionConfiguration;
    _multimediaLimit!: number;
    _lblTabKeywords!: LocalizedString;
    _lblTabProductInformation!: LocalizedString;
    _lblProductInformationDescription!: LocalizedString;
    _productType!: LocalizedString;
    _productTypePlaceholder!: LocalizedString;
    _lblPropertyValue!: LocalizedString;
    _propertyValuePlaceholder!: LocalizedString;
    _lblProductTypeChangeModalTitle!: LocalizedString;
    _lblProductTypeChangeModalText!: LocalizedString;
    _categoryBindingForProductType!: boolean;
    _lblProductCollection!: LocalizedString;
    _hntProductCollection!: LocalizedString;

    get titleEmptyProduct() {
        return this.jsonData["titleEmptyProduct"][this.lang]
    }

    get lblProductImage() {
        return this.jsonData["lblProductImage"][this.lang]
    }

    get lblProductImageMessage() {
        return this.jsonData["lblProductImageMessage"][this.lang]
    }

    get hntTitle() {
        return this.jsonData["hntTitle"][this.lang]
    }

    get hntPropImg() {
        return this.jsonData["hntPropImg"][this.lang]
    }

    get btnBrowseFiles() {
        return this.jsonData["btnBrowseFiles"][this.lang]
    }

    get btnDeleteImg() {
        return this.jsonData["btnDeleteImg"][this.lang]
    }

    get lblProductBackground() {
        return this.jsonData["lblProductBackground"][this.lang]
    }

    get hntTitleBackground() {
        return this.jsonData["hntTitleBackground"][this.lang]
    }

    get hntPropBackground() {
        return this.jsonData["hntPropBackground"][this.lang]
    }

    get btnBrowseFilesBackground() {
        return this.jsonData["btnBrowseFilesBackground"][this.lang]
    }

    get btnDeleteImgBackground() {
        return this.jsonData["btnDeleteImgBackground"][this.lang]
    }

    get lblProductname() {
        return this.jsonData["lblProductname"][this.lang]
    }

    get hntProductname() {
        return this.jsonData["hntProductname"][this.lang]
    }

    get lblProductPublicationStart() {
        return this.jsonData["lblProductPublicationStart"][this.lang]
    }

    get hntProductPublicationStart() {
        return this.jsonData["hntProductPublicationStart"][this.lang]
    }

    get lblProductPublicationEnd() {
        return this.jsonData["lblProductPublicationEnd"][this.lang]
    }

    get hntProductPublicationEnd() {
        return this.jsonData["hntProductPublicationEnd"][this.lang]
    }

    get lblDescriptionDetail() {
        return this.jsonData["lblDescriptionDetail"][this.lang]
    }

    get hntDescriptionDetail() {
        return this.jsonData["hntDescriptionDetail"][this.lang]
    }

    get lblDescriptionShort() {
        return this.jsonData["lblDescriptionShort"][this.lang]
    }

    get hntDescriptionShort() {
        return this.jsonData["hntDescriptionShort"][this.lang]
    }

    get lblTabCategories() {
        return this.jsonData["lblTabCategories"][this.lang]
    }

    get lblCategoriesDescription() {
        return this.jsonData["lblCategoriesDescription"][this.lang]
    }

    get categoryWhitelist() {
        return this.jsonData["categoryWhitelist"]
    }

    get lblTabLinks() {
        return this.jsonData["lblTabLinks"][this.lang]
    }

    get lblLinksDescription() {
        return this.jsonData["lblLinksDescription"][this.lang]
    }

    get lblTabMultimedia() {
        return this.jsonData["lblTabMultimedia"][this.lang]
    }

    get lblMultimediaDescription() {
        return this.jsonData["lblMultimediaDescription"][this.lang]
    }

    get multimediaSection() {
        if (!this._multimediaSection) {
            this._multimediaSection = new MultimediaImageSectionConfigurationImpl(this.jsonData["multimediaSection"], this.lang)
            this.subBrandings.push(this._multimediaSection as any as BrandingData)
        }
        return this._multimediaSection
    }

    get multimediaLimit() {
        return this.jsonData["multimediaLimit"]
    }

    get lblTabKeywords() {
        return this.jsonData["lblTabKeywords"][this.lang]
    }

    get lblTabProductInformation() {
        return this.jsonData["lblTabProductInformation"][this.lang]
    }

    get lblProductInformationDescription() {
        return this.jsonData["lblProductInformationDescription"][this.lang]
    }

    get productType() {
        return this.jsonData["productType"][this.lang]
    }

    get productTypePlaceholder() {
        return this.jsonData["productTypePlaceholder"][this.lang]
    }

    get lblPropertyValue() {
        return this.jsonData["lblPropertyValue"][this.lang]
    }

    get propertyValuePlaceholder() {
        return this.jsonData["propertyValuePlaceholder"][this.lang]
    }

    get lblProductTypeChangeModalTitle() {
        return this.jsonData["lblProductTypeChangeModalTitle"][this.lang]
    }

    get lblProductTypeChangeModalText() {
        return this.jsonData["lblProductTypeChangeModalText"][this.lang]
    }

    get categoryBindingForProductType() {
        return this.jsonData["categoryBindingForProductType"]
    }

    get lblProductCollection() {
        return this.jsonData["lblProductCollection"][this.lang]
    }

    get hntProductCollection() {
        return this.jsonData["hntProductCollection"][this.lang]
    }
}

class MultimediaImageSectionConfigurationImpl extends BrandingData implements MultimediaImageSectionConfiguration {
    _lblDropzoneTitle!: LocalizedString;
    _lblDropzoneDescription!: LocalizedString;
    _lblHeaderPosition!: LocalizedString;
    _lblHeaderPreview!: LocalizedString;
    _lblHeaderFile!: LocalizedString;
    _hntTitle!: LocalizedString;
    _hntSubtitle!: LocalizedString;

    get lblDropzoneTitle() {
        return this.jsonData["lblDropzoneTitle"][this.lang]
    }

    get lblDropzoneDescription() {
        return this.jsonData["lblDropzoneDescription"][this.lang]
    }

    get lblHeaderPosition() {
        return this.jsonData["lblHeaderPosition"][this.lang]
    }

    get lblHeaderPreview() {
        return this.jsonData["lblHeaderPreview"][this.lang]
    }

    get lblHeaderFile() {
        return this.jsonData["lblHeaderFile"][this.lang]
    }

    get hntTitle() {
        return this.jsonData["hntTitle"][this.lang]
    }

    get hntSubtitle() {
        return this.jsonData["hntSubtitle"][this.lang]
    }
}

class ProductsForCollectionsPageConfigurationImpl extends BrandingData implements ProductsForCollectionsPageConfiguration {
    _pageDescription!: LocalizedString;
    _btnAddProduct!: LocalizedString;
    _collectionCategoriesRootId!: string;

    get pageDescription() {
        return this.jsonData["pageDescription"][this.lang]
    }

    get btnAddProduct() {
        return this.jsonData["btnAddProduct"][this.lang]
    }

    get collectionCategoriesRootId() {
        return this.jsonData["collectionCategoriesRootId"]
    }
}

class TrademarksPageConfigurationImpl extends BrandingData implements TrademarksPageConfiguration {
    _pageDescription!: LocalizedString;
    _btnAddTrademark!: LocalizedString;
    _sctTrademark!: TrademarkSectionConfiguration;

    get pageDescription() {
        return this.jsonData["pageDescription"][this.lang]
    }

    get btnAddTrademark() {
        return this.jsonData["btnAddTrademark"][this.lang]
    }

    get sctTrademark() {
        if (!this._sctTrademark) {
            this._sctTrademark = new TrademarkSectionConfigurationImpl(this.jsonData["sctTrademark"], this.lang)
            this.subBrandings.push(this._sctTrademark as any as BrandingData)
        }
        return this._sctTrademark
    }
}

class TrademarkSectionConfigurationImpl extends BrandingData implements TrademarkSectionConfiguration {
    _titleEmptyTrademark!: LocalizedString;
    _lblTrademarkImage!: LocalizedString;
    _lblTrademarkImageMessage!: LocalizedString;
    _hntTitle!: LocalizedString;
    _hntProp!: LocalizedString;
    _btnBrowseFiles!: LocalizedString;
    _btnDeleteImg!: LocalizedString;
    _lblTrademarkBackground!: LocalizedString;
    _hntTitleBackground!: LocalizedString;
    _hntPropBackground!: LocalizedString;
    _btnBrowseFilesBackground!: LocalizedString;
    _btnDeleteImgBackground!: LocalizedString;
    _lblTrademarkname!: LocalizedString;
    _hntTrademarkname!: LocalizedString;
    _lblDescriptionDetail!: LocalizedString;
    _hntDescriptionDetail!: LocalizedString;
    _lblDescriptionShort!: LocalizedString;
    _hntDescriptionShort!: LocalizedString;
    _lblTabCategories!: LocalizedString;
    _lblCategoriesDescription!: LocalizedString;
    _categoryWhitelist!: string[];
    _lblTabMultimedia!: LocalizedString;
    _lblMultimediaDescription!: LocalizedString;
    _multimediaSection!: MultimediaImageSectionConfiguration;
    _multimediaLimit!: number;
    _lblTabKeywords!: LocalizedString;

    get titleEmptyTrademark() {
        return this.jsonData["titleEmptyTrademark"][this.lang]
    }

    get lblTrademarkImage() {
        return this.jsonData["lblTrademarkImage"][this.lang]
    }

    get lblTrademarkImageMessage() {
        return this.jsonData["lblTrademarkImageMessage"][this.lang]
    }

    get hntTitle() {
        return this.jsonData["hntTitle"][this.lang]
    }

    get hntProp() {
        return this.jsonData["hntProp"][this.lang]
    }

    get btnBrowseFiles() {
        return this.jsonData["btnBrowseFiles"][this.lang]
    }

    get btnDeleteImg() {
        return this.jsonData["btnDeleteImg"][this.lang]
    }

    get lblTrademarkBackground() {
        return this.jsonData["lblTrademarkBackground"][this.lang]
    }

    get hntTitleBackground() {
        return this.jsonData["hntTitleBackground"][this.lang]
    }

    get hntPropBackground() {
        return this.jsonData["hntPropBackground"][this.lang]
    }

    get btnBrowseFilesBackground() {
        return this.jsonData["btnBrowseFilesBackground"][this.lang]
    }

    get btnDeleteImgBackground() {
        return this.jsonData["btnDeleteImgBackground"][this.lang]
    }

    get lblTrademarkname() {
        return this.jsonData["lblTrademarkname"][this.lang]
    }

    get hntTrademarkname() {
        return this.jsonData["hntTrademarkname"][this.lang]
    }

    get lblDescriptionDetail() {
        return this.jsonData["lblDescriptionDetail"][this.lang]
    }

    get hntDescriptionDetail() {
        return this.jsonData["hntDescriptionDetail"][this.lang]
    }

    get lblDescriptionShort() {
        return this.jsonData["lblDescriptionShort"][this.lang]
    }

    get hntDescriptionShort() {
        return this.jsonData["hntDescriptionShort"][this.lang]
    }

    get lblTabCategories() {
        return this.jsonData["lblTabCategories"][this.lang]
    }

    get lblCategoriesDescription() {
        return this.jsonData["lblCategoriesDescription"][this.lang]
    }

    get categoryWhitelist() {
        return this.jsonData["categoryWhitelist"]
    }

    get lblTabMultimedia() {
        return this.jsonData["lblTabMultimedia"][this.lang]
    }

    get lblMultimediaDescription() {
        return this.jsonData["lblMultimediaDescription"][this.lang]
    }

    get multimediaSection() {
        if (!this._multimediaSection) {
            this._multimediaSection = new MultimediaImageSectionConfigurationImpl(this.jsonData["multimediaSection"], this.lang)
            this.subBrandings.push(this._multimediaSection as any as BrandingData)
        }
        return this._multimediaSection
    }

    get multimediaLimit() {
        return this.jsonData["multimediaLimit"]
    }

    get lblTabKeywords() {
        return this.jsonData["lblTabKeywords"][this.lang]
    }
}

class TeamPageConfigurationImpl extends BrandingData implements TeamPageConfiguration {
    _pageDescription!: LocalizedString;
    _btnAddSPerson!: LocalizedString;
    _sctPerson!: PersonSectionConfiguration;

    get pageDescription() {
        return this.jsonData["pageDescription"][this.lang]
    }

    get btnAddSPerson() {
        return this.jsonData["btnAddSPerson"][this.lang]
    }

    get sctPerson() {
        if (!this._sctPerson) {
            this._sctPerson = new PersonSectionConfigurationImpl(this.jsonData["sctPerson"], this.lang)
            this.subBrandings.push(this._sctPerson as any as BrandingData)
        }
        return this._sctPerson
    }
}

class PersonSectionConfigurationImpl extends BrandingData implements PersonSectionConfiguration {
    _titleEmptyPerson!: LocalizedString;
    _lblProfileImage!: LocalizedString;
    _lblProfileImageMessage!: LocalizedString;
    _hntTitle!: LocalizedString;
    _hntProp!: LocalizedString;
    _btnBrowseFiles!: LocalizedString;
    _btnDeleteImg!: LocalizedString;
    _lblPersonBackground!: LocalizedString;
    _hntTitleBackground!: LocalizedString;
    _hntPropBackground!: LocalizedString;
    _btnBrowseFilesBackground!: LocalizedString;
    _btnDeleteImgBackground!: LocalizedString;
    _lblTitleTeam!: LocalizedString;
    _hntTitleTeam!: LocalizedString;
    _lblFirstName!: LocalizedString;
    _hntFirstName!: LocalizedString;
    _lblLastName!: LocalizedString;
    _hntLastName!: LocalizedString;
    _lblRole!: LocalizedString;
    _hntRole!: LocalizedString;
    _personFunctionWhitelist!: string[];
    _lblPosition!: LocalizedString;
    _hntPosition!: LocalizedString;
    _lblCompany!: LocalizedString;
    _hntCompany!: LocalizedString;
    _lblPhone!: LocalizedString;
    _hntPhone!: LocalizedString;
    _lblPhoneVisibleOnProfile!: LocalizedString;
    _lblEmail!: LocalizedString;
    _hntEmail!: LocalizedString;
    _lblEmailVisibleOnProfile!: LocalizedString;
    _lblDescriptionDetail!: LocalizedString;
    _hntDescriptionDetail!: LocalizedString;
    _lblDescriptionShort!: LocalizedString;
    _hntDescriptionShort!: LocalizedString;

    get titleEmptyPerson() {
        return this.jsonData["titleEmptyPerson"][this.lang]
    }

    get lblProfileImage() {
        return this.jsonData["lblProfileImage"][this.lang]
    }

    get lblProfileImageMessage() {
        return this.jsonData["lblProfileImageMessage"][this.lang]
    }

    get hntTitle() {
        return this.jsonData["hntTitle"][this.lang]
    }

    get hntProp() {
        return this.jsonData["hntProp"][this.lang]
    }

    get btnBrowseFiles() {
        return this.jsonData["btnBrowseFiles"][this.lang]
    }

    get btnDeleteImg() {
        return this.jsonData["btnDeleteImg"][this.lang]
    }

    get lblPersonBackground() {
        return this.jsonData["lblPersonBackground"][this.lang]
    }

    get hntTitleBackground() {
        return this.jsonData["hntTitleBackground"][this.lang]
    }

    get hntPropBackground() {
        return this.jsonData["hntPropBackground"][this.lang]
    }

    get btnBrowseFilesBackground() {
        return this.jsonData["btnBrowseFilesBackground"][this.lang]
    }

    get btnDeleteImgBackground() {
        return this.jsonData["btnDeleteImgBackground"][this.lang]
    }

    get lblTitleTeam() {
        return this.jsonData["lblTitleTeam"][this.lang]
    }

    get hntTitleTeam() {
        return this.jsonData["hntTitleTeam"][this.lang]
    }

    get lblFirstName() {
        return this.jsonData["lblFirstName"][this.lang]
    }

    get hntFirstName() {
        return this.jsonData["hntFirstName"][this.lang]
    }

    get lblLastName() {
        return this.jsonData["lblLastName"][this.lang]
    }

    get hntLastName() {
        return this.jsonData["hntLastName"][this.lang]
    }

    get lblRole() {
        return this.jsonData["lblRole"][this.lang]
    }

    get hntRole() {
        return this.jsonData["hntRole"][this.lang]
    }

    get personFunctionWhitelist() {
        return this.jsonData["personFunctionWhitelist"]
    }

    get lblPosition() {
        return this.jsonData["lblPosition"][this.lang]
    }

    get hntPosition() {
        return this.jsonData["hntPosition"][this.lang]
    }

    get lblCompany() {
        return this.jsonData["lblCompany"][this.lang]
    }

    get hntCompany() {
        return this.jsonData["hntCompany"][this.lang]
    }

    get lblPhone() {
        return this.jsonData["lblPhone"][this.lang]
    }

    get hntPhone() {
        return this.jsonData["hntPhone"][this.lang]
    }

    get lblPhoneVisibleOnProfile() {
        return this.jsonData["lblPhoneVisibleOnProfile"][this.lang]
    }

    get lblEmail() {
        return this.jsonData["lblEmail"][this.lang]
    }

    get hntEmail() {
        return this.jsonData["hntEmail"][this.lang]
    }

    get lblEmailVisibleOnProfile() {
        return this.jsonData["lblEmailVisibleOnProfile"][this.lang]
    }

    get lblDescriptionDetail() {
        return this.jsonData["lblDescriptionDetail"][this.lang]
    }

    get hntDescriptionDetail() {
        return this.jsonData["hntDescriptionDetail"][this.lang]
    }

    get lblDescriptionShort() {
        return this.jsonData["lblDescriptionShort"][this.lang]
    }

    get hntDescriptionShort() {
        return this.jsonData["hntDescriptionShort"][this.lang]
    }
}

class MultimediaPageConfigurationImpl extends BrandingData implements MultimediaPageConfiguration {
    _pageDescription!: LocalizedString;
    _sctMultimediaImage!: MultimediaImageSectionConfiguration;

    get pageDescription() {
        return this.jsonData["pageDescription"][this.lang]
    }

    get sctMultimediaImage() {
        if (!this._sctMultimediaImage) {
            this._sctMultimediaImage = new MultimediaImageSectionConfigurationImpl(this.jsonData["sctMultimediaImage"], this.lang)
            this.subBrandings.push(this._sctMultimediaImage as any as BrandingData)
        }
        return this._sctMultimediaImage
    }
}

class ExhibitorNewsPageConfigurationImpl extends BrandingData implements ExhibitorNewsPageConfiguration {
    _pageDescription!: LocalizedString;
    _btnAddNews!: LocalizedString;
    _sctNews!: NewsSectionConfiguration;
    _categoryWhitelist!: string[];

    get pageDescription() {
        return this.jsonData["pageDescription"][this.lang]
    }

    get btnAddNews() {
        return this.jsonData["btnAddNews"][this.lang]
    }

    get sctNews() {
        if (!this._sctNews) {
            this._sctNews = new NewsSectionConfigurationImpl(this.jsonData["sctNews"], this.lang)
            this.subBrandings.push(this._sctNews as any as BrandingData)
        }
        return this._sctNews
    }

    get categoryWhitelist() {
        return this.jsonData["categoryWhitelist"]
    }
}

class NewsSectionConfigurationImpl extends BrandingData implements NewsSectionConfiguration {
    _titleEmptyNews!: LocalizedString;
    _lblNewsImage!: LocalizedString;
    _lblNewsImageMessage!: LocalizedString;
    _hntTitle!: LocalizedString;
    _hntPropImg!: LocalizedString;
    _btnBrowseFiles!: LocalizedString;
    _btnDeleteImg!: LocalizedString;
    _lblNewsBackground!: LocalizedString;
    _hntTitleBackground!: LocalizedString;
    _hntPropBackground!: LocalizedString;
    _btnBrowseFilesBackground!: LocalizedString;
    _btnDeleteImgBackground!: LocalizedString;
    _lblNewsTitle!: LocalizedString;
    _hntNewsTitle!: LocalizedString;
    _lblNewsDate!: LocalizedString;
    _hntNewsDate!: LocalizedString;
    _lblDescription!: LocalizedString;
    _hntDescription!: LocalizedString;
    _lblSubtitle!: LocalizedString;
    _hntSubtitle!: LocalizedString;
    _lblTabCategories!: LocalizedString;
    _lblCategoriesDescription!: LocalizedString;
    _lblTabMultimedia!: LocalizedString;
    _lblMultimediaDescription!: LocalizedString;
    _multimediaSection!: MultimediaImageSectionConfiguration;
    _multimediaLimit!: number;
    _lblTabKeywords!: LocalizedString;
    _lblSuppressOrganizationLinkage!: LocalizedString;

    get titleEmptyNews() {
        return this.jsonData["titleEmptyNews"][this.lang]
    }

    get lblNewsImage() {
        return this.jsonData["lblNewsImage"][this.lang]
    }

    get lblNewsImageMessage() {
        return this.jsonData["lblNewsImageMessage"][this.lang]
    }

    get hntTitle() {
        return this.jsonData["hntTitle"][this.lang]
    }

    get hntPropImg() {
        return this.jsonData["hntPropImg"][this.lang]
    }

    get btnBrowseFiles() {
        return this.jsonData["btnBrowseFiles"][this.lang]
    }

    get btnDeleteImg() {
        return this.jsonData["btnDeleteImg"][this.lang]
    }

    get lblNewsBackground() {
        return this.jsonData["lblNewsBackground"][this.lang]
    }

    get hntTitleBackground() {
        return this.jsonData["hntTitleBackground"][this.lang]
    }

    get hntPropBackground() {
        return this.jsonData["hntPropBackground"][this.lang]
    }

    get btnBrowseFilesBackground() {
        return this.jsonData["btnBrowseFilesBackground"][this.lang]
    }

    get btnDeleteImgBackground() {
        return this.jsonData["btnDeleteImgBackground"][this.lang]
    }

    get lblNewsTitle() {
        return this.jsonData["lblNewsTitle"][this.lang]
    }

    get hntNewsTitle() {
        return this.jsonData["hntNewsTitle"][this.lang]
    }

    get lblNewsDate() {
        return this.jsonData["lblNewsDate"][this.lang]
    }

    get hntNewsDate() {
        return this.jsonData["hntNewsDate"][this.lang]
    }

    get lblDescription() {
        return this.jsonData["lblDescription"][this.lang]
    }

    get hntDescription() {
        return this.jsonData["hntDescription"][this.lang]
    }

    get lblSubtitle() {
        return this.jsonData["lblSubtitle"][this.lang]
    }

    get hntSubtitle() {
        return this.jsonData["hntSubtitle"][this.lang]
    }

    get lblTabCategories() {
        return this.jsonData["lblTabCategories"][this.lang]
    }

    get lblCategoriesDescription() {
        return this.jsonData["lblCategoriesDescription"][this.lang]
    }

    get lblTabMultimedia() {
        return this.jsonData["lblTabMultimedia"][this.lang]
    }

    get lblMultimediaDescription() {
        return this.jsonData["lblMultimediaDescription"][this.lang]
    }

    get multimediaSection() {
        if (!this._multimediaSection) {
            this._multimediaSection = new MultimediaImageSectionConfigurationImpl(this.jsonData["multimediaSection"], this.lang)
            this.subBrandings.push(this._multimediaSection as any as BrandingData)
        }
        return this._multimediaSection
    }

    get multimediaLimit() {
        return this.jsonData["multimediaLimit"]
    }

    get lblTabKeywords() {
        return this.jsonData["lblTabKeywords"][this.lang]
    }

    get lblSuppressOrganizationLinkage() {
        return this.jsonData["lblSuppressOrganizationLinkage"][this.lang]
    }
}

class OrganizerNewsPageConfigurationImpl extends BrandingData implements OrganizerNewsPageConfiguration {
    _pageDescription!: LocalizedString;

    get pageDescription() {
        return this.jsonData["pageDescription"][this.lang]
    }
}

class StoryNewsPageConfigurationImpl extends BrandingData implements StoryNewsPageConfiguration {
    _pageDescription!: LocalizedString;
    _btnAddNews!: LocalizedString;
    _sctStory!: NewsSectionConfiguration;
    _categoryWhitelist!: string[];

    get pageDescription() {
        return this.jsonData["pageDescription"][this.lang]
    }

    get btnAddNews() {
        return this.jsonData["btnAddNews"][this.lang]
    }

    get sctStory() {
        if (!this._sctStory) {
            this._sctStory = new NewsSectionConfigurationImpl(this.jsonData["sctStory"], this.lang)
            this.subBrandings.push(this._sctStory as any as BrandingData)
        }
        return this._sctStory
    }

    get categoryWhitelist() {
        return this.jsonData["categoryWhitelist"]
    }
}

class EventDatesPageConfigurationImpl extends BrandingData implements EventDatesPageConfiguration {
    _pageDescription!: LocalizedString;
    _btnAddEventDate!: LocalizedString;
    _sctEventDates!: EventDatesSectionConfiguration;

    get pageDescription() {
        return this.jsonData["pageDescription"][this.lang]
    }

    get btnAddEventDate() {
        return this.jsonData["btnAddEventDate"][this.lang]
    }

    get sctEventDates() {
        if (!this._sctEventDates) {
            this._sctEventDates = new EventDatesSectionConfigurationImpl(this.jsonData["sctEventDates"], this.lang)
            this.subBrandings.push(this._sctEventDates as any as BrandingData)
        }
        return this._sctEventDates
    }
}

class EventDatesSectionConfigurationImpl extends BrandingData implements EventDatesSectionConfiguration {
    _titleEmptyEventDate!: LocalizedString;
    _lblEventDateImage!: LocalizedString;
    _lblEventDateImageMessage!: LocalizedString;
    _hntTitle!: LocalizedString;
    _hntPropImg!: LocalizedString;
    _btnBrowseFiles!: LocalizedString;
    _btnDeleteImg!: LocalizedString;
    _lblEventDateBackgroundImage!: LocalizedString;
    _hntTitleEventGuideBackgroundImage!: LocalizedString;
    _hntPropEventGuideBackgroundImage!: LocalizedString;
    _hntTitleWidgetBackgroundImage!: LocalizedString;
    _hntPropWidgetBackgroundImage!: LocalizedString;
    _hntTitleMobileBackgroundImage!: LocalizedString;
    _hntPropMobileBackgroundImage!: LocalizedString;
    _btnBrowseFilesBackgroundImage!: LocalizedString;
    _btnDeleteImgBackgroundImage!: LocalizedString;
    _lblEventdateTitle!: LocalizedString;
    _hntEventdateTitle!: LocalizedString;
    _lblDate!: LocalizedString;
    _hntDate!: LocalizedString;
    _lblStartTime!: LocalizedString;
    _lblEndTime!: LocalizedString;
    _lblLocation!: LocalizedString;
    _hntLocation!: LocalizedString;
    _lblEventType!: LocalizedString;
    _hntEventType!: LocalizedString;
    _lblPartEventSeries!: LocalizedString;
    _hntPartEventSeries!: LocalizedString;
    _lblListenon!: LocalizedString;
    _lblListenonTRUE!: LocalizedString;
    _lblListenonFALSE!: LocalizedString;
    _lblBreakoutRoom!: LocalizedString;
    _lblDescription!: LocalizedString;
    _hntDescription!: LocalizedString;
    _lblSubtitle!: LocalizedString;
    _hntSubtitle!: LocalizedString;
    _lblTabSpeakers!: LocalizedString;
    _lblSpeakersDescription!: LocalizedString;
    _personFunctionWhitelist!: string[];
    _lblTabMultimedia!: LocalizedString;
    _lblMultimediaDescription!: LocalizedString;
    _multimediaSection!: MultimediaImageSectionConfiguration;
    _multimediaLimit!: number;
    _lblTabKeywords!: LocalizedString;
    _lblTabPrerecorded!: LocalizedString;
    _lblPrerecordedDescription!: LocalizedString;
    _hntDropzoneTitlePrerecorded!: LocalizedString;
    _hntDropzonePropPrerecorded!: LocalizedString;

    get titleEmptyEventDate() {
        return this.jsonData["titleEmptyEventDate"][this.lang]
    }

    get lblEventDateImage() {
        return this.jsonData["lblEventDateImage"][this.lang]
    }

    get lblEventDateImageMessage() {
        return this.jsonData["lblEventDateImageMessage"][this.lang]
    }

    get hntTitle() {
        return this.jsonData["hntTitle"][this.lang]
    }

    get hntPropImg() {
        return this.jsonData["hntPropImg"][this.lang]
    }

    get btnBrowseFiles() {
        return this.jsonData["btnBrowseFiles"][this.lang]
    }

    get btnDeleteImg() {
        return this.jsonData["btnDeleteImg"][this.lang]
    }

    get lblEventDateBackgroundImage() {
        return this.jsonData["lblEventDateBackgroundImage"][this.lang]
    }

    get hntTitleEventGuideBackgroundImage() {
        return this.jsonData["hntTitleEventGuideBackgroundImage"][this.lang]
    }

    get hntPropEventGuideBackgroundImage() {
        return this.jsonData["hntPropEventGuideBackgroundImage"][this.lang]
    }

    get hntTitleWidgetBackgroundImage() {
        return this.jsonData["hntTitleWidgetBackgroundImage"][this.lang]
    }

    get hntPropWidgetBackgroundImage() {
        return this.jsonData["hntPropWidgetBackgroundImage"][this.lang]
    }

    get hntTitleMobileBackgroundImage() {
        return this.jsonData["hntTitleMobileBackgroundImage"][this.lang]
    }

    get hntPropMobileBackgroundImage() {
        return this.jsonData["hntPropMobileBackgroundImage"][this.lang]
    }

    get btnBrowseFilesBackgroundImage() {
        return this.jsonData["btnBrowseFilesBackgroundImage"][this.lang]
    }

    get btnDeleteImgBackgroundImage() {
        return this.jsonData["btnDeleteImgBackgroundImage"][this.lang]
    }

    get lblEventdateTitle() {
        return this.jsonData["lblEventdateTitle"][this.lang]
    }

    get hntEventdateTitle() {
        return this.jsonData["hntEventdateTitle"][this.lang]
    }

    get lblDate() {
        return this.jsonData["lblDate"][this.lang]
    }

    get hntDate() {
        return this.jsonData["hntDate"][this.lang]
    }

    get lblStartTime() {
        return this.jsonData["lblStartTime"][this.lang]
    }

    get lblEndTime() {
        return this.jsonData["lblEndTime"][this.lang]
    }

    get lblLocation() {
        return this.jsonData["lblLocation"][this.lang]
    }

    get hntLocation() {
        return this.jsonData["hntLocation"][this.lang]
    }

    get lblEventType() {
        return this.jsonData["lblEventType"][this.lang]
    }

    get hntEventType() {
        return this.jsonData["hntEventType"][this.lang]
    }

    get lblPartEventSeries() {
        return this.jsonData["lblPartEventSeries"][this.lang]
    }

    get hntPartEventSeries() {
        return this.jsonData["hntPartEventSeries"][this.lang]
    }

    get lblListenon() {
        return this.jsonData["lblListenon"][this.lang]
    }

    get lblListenonTRUE() {
        return this.jsonData["lblListenonTRUE"][this.lang]
    }

    get lblListenonFALSE() {
        return this.jsonData["lblListenonFALSE"][this.lang]
    }

    get lblBreakoutRoom() {
        return this.jsonData["lblBreakoutRoom"][this.lang]
    }

    get lblDescription() {
        return this.jsonData["lblDescription"][this.lang]
    }

    get hntDescription() {
        return this.jsonData["hntDescription"][this.lang]
    }

    get lblSubtitle() {
        return this.jsonData["lblSubtitle"][this.lang]
    }

    get hntSubtitle() {
        return this.jsonData["hntSubtitle"][this.lang]
    }

    get lblTabSpeakers() {
        return this.jsonData["lblTabSpeakers"][this.lang]
    }

    get lblSpeakersDescription() {
        return this.jsonData["lblSpeakersDescription"][this.lang]
    }

    get personFunctionWhitelist() {
        return this.jsonData["personFunctionWhitelist"]
    }

    get lblTabMultimedia() {
        return this.jsonData["lblTabMultimedia"][this.lang]
    }

    get lblMultimediaDescription() {
        return this.jsonData["lblMultimediaDescription"][this.lang]
    }

    get multimediaSection() {
        if (!this._multimediaSection) {
            this._multimediaSection = new MultimediaImageSectionConfigurationImpl(this.jsonData["multimediaSection"], this.lang)
            this.subBrandings.push(this._multimediaSection as any as BrandingData)
        }
        return this._multimediaSection
    }

    get multimediaLimit() {
        return this.jsonData["multimediaLimit"]
    }

    get lblTabKeywords() {
        return this.jsonData["lblTabKeywords"][this.lang]
    }

    get lblTabPrerecorded() {
        return this.jsonData["lblTabPrerecorded"][this.lang]
    }

    get lblPrerecordedDescription() {
        return this.jsonData["lblPrerecordedDescription"][this.lang]
    }

    get hntDropzoneTitlePrerecorded() {
        return this.jsonData["hntDropzoneTitlePrerecorded"][this.lang]
    }

    get hntDropzonePropPrerecorded() {
        return this.jsonData["hntDropzonePropPrerecorded"][this.lang]
    }
}

class NonCuratedEventDatesPageConfigurationImpl extends BrandingData implements NonCuratedEventDatesPageConfiguration {
    _pageDescription!: LocalizedString;
    _btnAddEventDate!: LocalizedString;
    _sctEventDates!: EventDatesSectionConfiguration;
    _btnAddSpeaker!: LocalizedString;
    _lblSpeakersModalTitle!: LocalizedString;
    _lblSpeakersModalSpeakersListTitle!: LocalizedString;
    _lblSpeakersModalFunctionsListTitle!: LocalizedString;
    _lblSpeakersModalConfirm!: LocalizedString;
    _lblSpeakersModalCancel!: LocalizedString;

    get pageDescription() {
        return this.jsonData["pageDescription"][this.lang]
    }

    get btnAddEventDate() {
        return this.jsonData["btnAddEventDate"][this.lang]
    }

    get sctEventDates() {
        if (!this._sctEventDates) {
            this._sctEventDates = new EventDatesSectionConfigurationImpl(this.jsonData["sctEventDates"], this.lang)
            this.subBrandings.push(this._sctEventDates as any as BrandingData)
        }
        return this._sctEventDates
    }

    get btnAddSpeaker() {
        return this.jsonData["btnAddSpeaker"][this.lang]
    }

    get lblSpeakersModalTitle() {
        return this.jsonData["lblSpeakersModalTitle"][this.lang]
    }

    get lblSpeakersModalSpeakersListTitle() {
        return this.jsonData["lblSpeakersModalSpeakersListTitle"][this.lang]
    }

    get lblSpeakersModalFunctionsListTitle() {
        return this.jsonData["lblSpeakersModalFunctionsListTitle"][this.lang]
    }

    get lblSpeakersModalConfirm() {
        return this.jsonData["lblSpeakersModalConfirm"][this.lang]
    }

    get lblSpeakersModalCancel() {
        return this.jsonData["lblSpeakersModalCancel"][this.lang]
    }
}

class ShowroomsPageConfigurationImpl extends BrandingData implements ShowroomsPageConfiguration {
    _pageTitlePublic!: LocalizedString;
    _pageDescriptionPublic!: LocalizedString;
    _pageTitlePrivate!: LocalizedString;
    _pageDescriptionPrivate!: LocalizedString;
    _btnAddShowrooms!: LocalizedString;
    _sctShowrooms!: ShowroomsSectionConfiguration;

    get pageTitlePublic() {
        return this.jsonData["pageTitlePublic"][this.lang]
    }

    get pageDescriptionPublic() {
        return this.jsonData["pageDescriptionPublic"][this.lang]
    }

    get pageTitlePrivate() {
        return this.jsonData["pageTitlePrivate"][this.lang]
    }

    get pageDescriptionPrivate() {
        return this.jsonData["pageDescriptionPrivate"][this.lang]
    }

    get btnAddShowrooms() {
        return this.jsonData["btnAddShowrooms"][this.lang]
    }

    get sctShowrooms() {
        if (!this._sctShowrooms) {
            this._sctShowrooms = new ShowroomsSectionConfigurationImpl(this.jsonData["sctShowrooms"], this.lang)
            this.subBrandings.push(this._sctShowrooms as any as BrandingData)
        }
        return this._sctShowrooms
    }
}

class ShowroomsSectionConfigurationImpl extends BrandingData implements ShowroomsSectionConfiguration {
    _titleEmptyShowrooms!: LocalizedString;
    _lblShowroomsImage!: LocalizedString;
    _lblShowroomsImageMessage!: LocalizedString;
    _hntTitle!: LocalizedString;
    _hntProp!: LocalizedString;
    _btnBrowseFiles!: LocalizedString;
    _btnDeleteImg!: LocalizedString;
    _lblShowroomstitle!: LocalizedString;
    _hntShowroomstitle!: LocalizedString;
    _lblDescription!: LocalizedString;
    _hntDescription!: LocalizedString;
    _lblAccessMethod!: LocalizedString;
    _hntAccessMethod!: LocalizedString;
    _titleMockShowroomEn!: string;
    _titleMockShowroomDe!: string;

    get titleEmptyShowrooms() {
        return this.jsonData["titleEmptyShowrooms"][this.lang]
    }

    get lblShowroomsImage() {
        return this.jsonData["lblShowroomsImage"][this.lang]
    }

    get lblShowroomsImageMessage() {
        return this.jsonData["lblShowroomsImageMessage"][this.lang]
    }

    get hntTitle() {
        return this.jsonData["hntTitle"][this.lang]
    }

    get hntProp() {
        return this.jsonData["hntProp"][this.lang]
    }

    get btnBrowseFiles() {
        return this.jsonData["btnBrowseFiles"][this.lang]
    }

    get btnDeleteImg() {
        return this.jsonData["btnDeleteImg"][this.lang]
    }

    get lblShowroomstitle() {
        return this.jsonData["lblShowroomstitle"][this.lang]
    }

    get hntShowroomstitle() {
        return this.jsonData["hntShowroomstitle"][this.lang]
    }

    get lblDescription() {
        return this.jsonData["lblDescription"][this.lang]
    }

    get hntDescription() {
        return this.jsonData["hntDescription"][this.lang]
    }

    get lblAccessMethod() {
        return this.jsonData["lblAccessMethod"][this.lang]
    }

    get hntAccessMethod() {
        return this.jsonData["hntAccessMethod"][this.lang]
    }

    get titleMockShowroomEn() {
        return this.jsonData["titleMockShowroomEn"]
    }

    get titleMockShowroomDe() {
        return this.jsonData["titleMockShowroomDe"]
    }
}

class CouponsPageConfigurationImpl extends BrandingData implements CouponsPageConfiguration {
    _pageDescription!: LocalizedString;
    _btnAddCoupons!: LocalizedString;
    _sctCoupons!: CouponsSectionConfiguration;

    get pageDescription() {
        return this.jsonData["pageDescription"][this.lang]
    }

    get btnAddCoupons() {
        return this.jsonData["btnAddCoupons"][this.lang]
    }

    get sctCoupons() {
        if (!this._sctCoupons) {
            this._sctCoupons = new CouponsSectionConfigurationImpl(this.jsonData["sctCoupons"], this.lang)
            this.subBrandings.push(this._sctCoupons as any as BrandingData)
        }
        return this._sctCoupons
    }
}

class CouponsSectionConfigurationImpl extends BrandingData implements CouponsSectionConfiguration {
    _nameEmptyCoupons!: LocalizedString;
    _lblCouponsImage!: LocalizedString;
    _lblCouponsImageMessage!: LocalizedString;
    _hntPlaceholderTitle!: LocalizedString;
    _hntPlaceholderProp!: LocalizedString;
    _btnBrowseFiles!: LocalizedString;
    _btnDeleteImg!: LocalizedString;
    _lblCouponsName!: LocalizedString;
    _hntCouponsName!: LocalizedString;
    _lblURL!: LocalizedString;
    _hntURL!: LocalizedString;
    _lblCouponsValidStart!: LocalizedString;
    _hntCouponsValidStart!: LocalizedString;
    _lblCouponsValidEnd!: LocalizedString;
    _hntCouponsValidEnd!: LocalizedString;
    _lblDescriptionTitle!: LocalizedString;
    _hntDescriptionTitle!: LocalizedString;
    _lblDescriptionLong!: LocalizedString;
    _hntDescriptionLong!: LocalizedString;
    _lblDescriptionShort!: LocalizedString;
    _hntDescriptionShort!: LocalizedString;
    _dateCouponsValidStart!: string;
    _dateCouponsValidEnd!: string;
    _lblTabMultimedia!: LocalizedString;
    _lblMultimediaDescription!: LocalizedString;
    _multimediaSection!: MultimediaImageSectionConfiguration;
    _multimediaLimit!: number;

    get nameEmptyCoupons() {
        return this.jsonData["nameEmptyCoupons"][this.lang]
    }

    get lblCouponsImage() {
        return this.jsonData["lblCouponsImage"][this.lang]
    }

    get lblCouponsImageMessage() {
        return this.jsonData["lblCouponsImageMessage"][this.lang]
    }

    get hntPlaceholderTitle() {
        return this.jsonData["hntPlaceholderTitle"][this.lang]
    }

    get hntPlaceholderProp() {
        return this.jsonData["hntPlaceholderProp"][this.lang]
    }

    get btnBrowseFiles() {
        return this.jsonData["btnBrowseFiles"][this.lang]
    }

    get btnDeleteImg() {
        return this.jsonData["btnDeleteImg"][this.lang]
    }

    get lblCouponsName() {
        return this.jsonData["lblCouponsName"][this.lang]
    }

    get hntCouponsName() {
        return this.jsonData["hntCouponsName"][this.lang]
    }

    get lblURL() {
        return this.jsonData["lblURL"][this.lang]
    }

    get hntURL() {
        return this.jsonData["hntURL"][this.lang]
    }

    get lblCouponsValidStart() {
        return this.jsonData["lblCouponsValidStart"][this.lang]
    }

    get hntCouponsValidStart() {
        return this.jsonData["hntCouponsValidStart"][this.lang]
    }

    get lblCouponsValidEnd() {
        return this.jsonData["lblCouponsValidEnd"][this.lang]
    }

    get hntCouponsValidEnd() {
        return this.jsonData["hntCouponsValidEnd"][this.lang]
    }

    get lblDescriptionTitle() {
        return this.jsonData["lblDescriptionTitle"][this.lang]
    }

    get hntDescriptionTitle() {
        return this.jsonData["hntDescriptionTitle"][this.lang]
    }

    get lblDescriptionLong() {
        return this.jsonData["lblDescriptionLong"][this.lang]
    }

    get hntDescriptionLong() {
        return this.jsonData["hntDescriptionLong"][this.lang]
    }

    get lblDescriptionShort() {
        return this.jsonData["lblDescriptionShort"][this.lang]
    }

    get hntDescriptionShort() {
        return this.jsonData["hntDescriptionShort"][this.lang]
    }

    get dateCouponsValidStart() {
        return this.jsonData["dateCouponsValidStart"]
    }

    get dateCouponsValidEnd() {
        return this.jsonData["dateCouponsValidEnd"]
    }

    get lblTabMultimedia() {
        return this.jsonData["lblTabMultimedia"][this.lang]
    }

    get lblMultimediaDescription() {
        return this.jsonData["lblMultimediaDescription"][this.lang]
    }

    get multimediaSection() {
        if (!this._multimediaSection) {
            this._multimediaSection = new MultimediaImageSectionConfigurationImpl(this.jsonData["multimediaSection"], this.lang)
            this.subBrandings.push(this._multimediaSection as any as BrandingData)
        }
        return this._multimediaSection
    }

    get multimediaLimit() {
        return this.jsonData["multimediaLimit"]
    }
}

class JobsPageConfigurationImpl extends BrandingData implements JobsPageConfiguration {
    _pageDescription!: LocalizedString;
    _btnAddJobs!: LocalizedString;
    _sctJobs!: JobsSectionConfiguration;

    get pageDescription() {
        return this.jsonData["pageDescription"][this.lang]
    }

    get btnAddJobs() {
        return this.jsonData["btnAddJobs"][this.lang]
    }

    get sctJobs() {
        if (!this._sctJobs) {
            this._sctJobs = new JobsSectionConfigurationImpl(this.jsonData["sctJobs"], this.lang)
            this.subBrandings.push(this._sctJobs as any as BrandingData)
        }
        return this._sctJobs
    }
}

class JobsSectionConfigurationImpl extends BrandingData implements JobsSectionConfiguration {
    _labelJobs!: LocalizedString;
    _titleEmptyJobs!: LocalizedString;
    _lblTitleJob!: LocalizedString;
    _hntTitleJob!: LocalizedString;
    _lblJobDescription!: LocalizedString;
    _hntJobDescription!: LocalizedString;
    _lblJobsImage!: LocalizedString;
    _lblJobsImageMessage!: LocalizedString;
    _hntTitle!: LocalizedString;
    _hntPropImg!: LocalizedString;
    _btnBrowseFiles!: LocalizedString;
    _btnDeleteImg!: LocalizedString;
    _lblJobBackgroundImage!: LocalizedString;
    _btnDeleteJobBackgroundImg!: LocalizedString;
    _hntTitleBackground!: LocalizedString;
    _hntPropBackground!: LocalizedString;
    _lblStartDisplayinOnDate!: LocalizedString;
    _hntStartDisplayinOnDate!: LocalizedString;
    _lblEndDisplayinOnDate!: LocalizedString;
    _hntEndDisplayinOnDate!: LocalizedString;
    _lblJobStartDate!: LocalizedString;
    _hntJobStartDate!: LocalizedString;
    _lblJobLocation!: LocalizedString;
    _hntJobLocation!: LocalizedString;
    _lblEmploymentType!: LocalizedString;
    _hntEmploymentType!: LocalizedString;
    _employmentType!: ValidationFieldNames[];
    _lblEmploymentLevel!: LocalizedString;
    _hntEmploymentLevel!: LocalizedString;
    _careerLevel!: ValidationFieldNames[];
    _lblSalaryGroup!: LocalizedString;
    _hntSalaryGroup!: LocalizedString;
    _salaryGroup!: ValidationFieldNames[];
    _lblJobReqirements!: LocalizedString;
    _hntJobReqirements!: LocalizedString;
    _lblTabCategories!: LocalizedString;
    _lblCategoriesDescription!: LocalizedString;
    _categoryWhitelist!: string[];
    _lblTabLinks!: LocalizedString;
    _lblLinksDescription!: LocalizedString;
    _lblTabContacts!: LocalizedString;
    _lblContactsDescription!: LocalizedString;
    _btnAddPerson!: LocalizedString;
    _personFunctionWhitelist!: string[];
    _lblTabMultimedia!: LocalizedString;
    _lblMultimediaDescription!: LocalizedString;
    _multimediaSection!: MultimediaImageSectionConfiguration;
    _multimediaLimit!: number;
    _lblHomeoffice!: LocalizedString;
    _lblHomeofficeNA!: LocalizedString;
    _lblHomeofficeNO!: LocalizedString;
    _lblHomeofficeYES!: LocalizedString;

    get labelJobs() {
        return this.jsonData["labelJobs"][this.lang]
    }

    get titleEmptyJobs() {
        return this.jsonData["titleEmptyJobs"][this.lang]
    }

    get lblTitleJob() {
        return this.jsonData["lblTitleJob"][this.lang]
    }

    get hntTitleJob() {
        return this.jsonData["hntTitleJob"][this.lang]
    }

    get lblJobDescription() {
        return this.jsonData["lblJobDescription"][this.lang]
    }

    get hntJobDescription() {
        return this.jsonData["hntJobDescription"][this.lang]
    }

    get lblJobsImage() {
        return this.jsonData["lblJobsImage"][this.lang]
    }

    get lblJobsImageMessage() {
        return this.jsonData["lblJobsImageMessage"][this.lang]
    }

    get hntTitle() {
        return this.jsonData["hntTitle"][this.lang]
    }

    get hntPropImg() {
        return this.jsonData["hntPropImg"][this.lang]
    }

    get btnBrowseFiles() {
        return this.jsonData["btnBrowseFiles"][this.lang]
    }

    get btnDeleteImg() {
        return this.jsonData["btnDeleteImg"][this.lang]
    }

    get lblJobBackgroundImage() {
        return this.jsonData["lblJobBackgroundImage"][this.lang]
    }

    get btnDeleteJobBackgroundImg() {
        return this.jsonData["btnDeleteJobBackgroundImg"][this.lang]
    }

    get hntTitleBackground() {
        return this.jsonData["hntTitleBackground"][this.lang]
    }

    get hntPropBackground() {
        return this.jsonData["hntPropBackground"][this.lang]
    }

    get lblStartDisplayinOnDate() {
        return this.jsonData["lblStartDisplayinOnDate"][this.lang]
    }

    get hntStartDisplayinOnDate() {
        return this.jsonData["hntStartDisplayinOnDate"][this.lang]
    }

    get lblEndDisplayinOnDate() {
        return this.jsonData["lblEndDisplayinOnDate"][this.lang]
    }

    get hntEndDisplayinOnDate() {
        return this.jsonData["hntEndDisplayinOnDate"][this.lang]
    }

    get lblJobStartDate() {
        return this.jsonData["lblJobStartDate"][this.lang]
    }

    get hntJobStartDate() {
        return this.jsonData["hntJobStartDate"][this.lang]
    }

    get lblJobLocation() {
        return this.jsonData["lblJobLocation"][this.lang]
    }

    get hntJobLocation() {
        return this.jsonData["hntJobLocation"][this.lang]
    }

    get lblEmploymentType() {
        return this.jsonData["lblEmploymentType"][this.lang]
    }

    get hntEmploymentType() {
        return this.jsonData["hntEmploymentType"][this.lang]
    }

    get employmentType() {
        if (!this._employmentType) {
            this._employmentType = []
            for (const arrayElement of this.jsonData["employmentType"]) {
                const arrayObject = new ValidationFieldNamesImpl(arrayElement, this.lang)
                this.employmentType.push(arrayObject)
                this.subBrandings.push(arrayObject as any as BrandingData)
            }
        }
        return this._employmentType
    }

    get lblEmploymentLevel() {
        return this.jsonData["lblEmploymentLevel"][this.lang]
    }

    get hntEmploymentLevel() {
        return this.jsonData["hntEmploymentLevel"][this.lang]
    }

    get careerLevel() {
        if (!this._careerLevel) {
            this._careerLevel = []
            for (const arrayElement of this.jsonData["careerLevel"]) {
                const arrayObject = new ValidationFieldNamesImpl(arrayElement, this.lang)
                this.careerLevel.push(arrayObject)
                this.subBrandings.push(arrayObject as any as BrandingData)
            }
        }
        return this._careerLevel
    }

    get lblSalaryGroup() {
        return this.jsonData["lblSalaryGroup"][this.lang]
    }

    get hntSalaryGroup() {
        return this.jsonData["hntSalaryGroup"][this.lang]
    }

    get salaryGroup() {
        if (!this._salaryGroup) {
            this._salaryGroup = []
            for (const arrayElement of this.jsonData["salaryGroup"]) {
                const arrayObject = new ValidationFieldNamesImpl(arrayElement, this.lang)
                this.salaryGroup.push(arrayObject)
                this.subBrandings.push(arrayObject as any as BrandingData)
            }
        }
        return this._salaryGroup
    }

    get lblJobReqirements() {
        return this.jsonData["lblJobReqirements"][this.lang]
    }

    get hntJobReqirements() {
        return this.jsonData["hntJobReqirements"][this.lang]
    }

    get lblTabCategories() {
        return this.jsonData["lblTabCategories"][this.lang]
    }

    get lblCategoriesDescription() {
        return this.jsonData["lblCategoriesDescription"][this.lang]
    }

    get categoryWhitelist() {
        return this.jsonData["categoryWhitelist"]
    }

    get lblTabLinks() {
        return this.jsonData["lblTabLinks"][this.lang]
    }

    get lblLinksDescription() {
        return this.jsonData["lblLinksDescription"][this.lang]
    }

    get lblTabContacts() {
        return this.jsonData["lblTabContacts"][this.lang]
    }

    get lblContactsDescription() {
        return this.jsonData["lblContactsDescription"][this.lang]
    }

    get btnAddPerson() {
        return this.jsonData["btnAddPerson"][this.lang]
    }

    get personFunctionWhitelist() {
        return this.jsonData["personFunctionWhitelist"]
    }

    get lblTabMultimedia() {
        return this.jsonData["lblTabMultimedia"][this.lang]
    }

    get lblMultimediaDescription() {
        return this.jsonData["lblMultimediaDescription"][this.lang]
    }

    get multimediaSection() {
        if (!this._multimediaSection) {
            this._multimediaSection = new MultimediaImageSectionConfigurationImpl(this.jsonData["multimediaSection"], this.lang)
            this.subBrandings.push(this._multimediaSection as any as BrandingData)
        }
        return this._multimediaSection
    }

    get multimediaLimit() {
        return this.jsonData["multimediaLimit"]
    }

    get lblHomeoffice() {
        return this.jsonData["lblHomeoffice"][this.lang]
    }

    get lblHomeofficeNA() {
        return this.jsonData["lblHomeofficeNA"][this.lang]
    }

    get lblHomeofficeNO() {
        return this.jsonData["lblHomeofficeNO"][this.lang]
    }

    get lblHomeofficeYES() {
        return this.jsonData["lblHomeofficeYES"][this.lang]
    }
}

class NotificationTemplatesPageConfigurationImpl extends BrandingData implements NotificationTemplatesPageConfiguration {
    _pageDescription!: LocalizedString;
    _btnAddNotificationTemplates!: LocalizedString;
    _sctNotificationTemplates!: NotificationsSectionConfiguration;

    get pageDescription() {
        return this.jsonData["pageDescription"][this.lang]
    }

    get btnAddNotificationTemplates() {
        return this.jsonData["btnAddNotificationTemplates"][this.lang]
    }

    get sctNotificationTemplates() {
        if (!this._sctNotificationTemplates) {
            this._sctNotificationTemplates = new NotificationsSectionConfigurationImpl(this.jsonData["sctNotificationTemplates"], this.lang)
            this.subBrandings.push(this._sctNotificationTemplates as any as BrandingData)
        }
        return this._sctNotificationTemplates
    }
}

class NotificationsSectionConfigurationImpl extends BrandingData implements NotificationsSectionConfiguration {
    _titleEmptyNotificationTemplate!: LocalizedString;
    _lblDate!: LocalizedString;
    _hntDate!: LocalizedString;
    _lblTime!: LocalizedString;
    _hntTime!: LocalizedString;
    _infoTime!: LocalizedString;
    _lblDestination!: LocalizedString;
    _hntDestination!: LocalizedString;
    _lblText!: LocalizedString;
    _hntText!: LocalizedString;
    _tmpText!: string;
    _tmpTextDe!: string;
    _lblMaxUsageCount!: LocalizedString;
    _hntMaxUsageCount!: LocalizedString;
    _lblCriteriaList!: LocalizedString;
    _hntCriteriaList!: LocalizedString;
    _msgCriteriaList!: LocalizedString;
    _lblPackageModalTitle!: LocalizedString;
    _lblPackageModalDescription!: LocalizedString;
    _lblPackageModalConfirm!: LocalizedString;
    _lblPackageModalCancel!: LocalizedString;
    _lblBlockTriggerModalTitle!: LocalizedString;
    _lblBlockTriggerModalDescription!: LocalizedString;
    _showDateTimeSection!: boolean;
    _dates!: NotificationTemplateDate[];
    _showTimePicker!: boolean;
    _times!: NotificationTemplateTime[];
    _lblEntireDay!: LocalizedString;
    _defaultStartDate!: string;
    _defaultEndDate!: string;
    _titleEmptyCoupon!: LocalizedString;
    _lblCouponsName!: LocalizedString;
    _hntCouponsName!: LocalizedString;
    _lblDescriptionTitle!: LocalizedString;
    _hntDescriptionTitle!: LocalizedString;
    _lblDescriptionLong!: LocalizedString;
    _hntDescriptionLong!: LocalizedString;

    get titleEmptyNotificationTemplate() {
        return this.jsonData["titleEmptyNotificationTemplate"][this.lang]
    }

    get lblDate() {
        return this.jsonData["lblDate"][this.lang]
    }

    get hntDate() {
        return this.jsonData["hntDate"][this.lang]
    }

    get lblTime() {
        return this.jsonData["lblTime"][this.lang]
    }

    get hntTime() {
        return this.jsonData["hntTime"][this.lang]
    }

    get infoTime() {
        return this.jsonData["infoTime"][this.lang]
    }

    get lblDestination() {
        return this.jsonData["lblDestination"][this.lang]
    }

    get hntDestination() {
        return this.jsonData["hntDestination"][this.lang]
    }

    get lblText() {
        return this.jsonData["lblText"][this.lang]
    }

    get hntText() {
        return this.jsonData["hntText"][this.lang]
    }

    get tmpText() {
        return this.jsonData["tmpText"]
    }

    get tmpTextDe() {
        return this.jsonData["tmpTextDe"]
    }

    get lblMaxUsageCount() {
        return this.jsonData["lblMaxUsageCount"][this.lang]
    }

    get hntMaxUsageCount() {
        return this.jsonData["hntMaxUsageCount"][this.lang]
    }

    get lblCriteriaList() {
        return this.jsonData["lblCriteriaList"][this.lang]
    }

    get hntCriteriaList() {
        return this.jsonData["hntCriteriaList"][this.lang]
    }

    get msgCriteriaList() {
        return this.jsonData["msgCriteriaList"][this.lang]
    }

    get lblPackageModalTitle() {
        return this.jsonData["lblPackageModalTitle"][this.lang]
    }

    get lblPackageModalDescription() {
        return this.jsonData["lblPackageModalDescription"][this.lang]
    }

    get lblPackageModalConfirm() {
        return this.jsonData["lblPackageModalConfirm"][this.lang]
    }

    get lblPackageModalCancel() {
        return this.jsonData["lblPackageModalCancel"][this.lang]
    }

    get lblBlockTriggerModalTitle() {
        return this.jsonData["lblBlockTriggerModalTitle"][this.lang]
    }

    get lblBlockTriggerModalDescription() {
        return this.jsonData["lblBlockTriggerModalDescription"][this.lang]
    }

    get showDateTimeSection() {
        return this.jsonData["showDateTimeSection"]
    }

    get dates() {
        if (!this._dates) {
            this._dates = []
            for (const arrayElement of this.jsonData["dates"]) {
                const arrayObject = new NotificationTemplateDateImpl(arrayElement, this.lang)
                this.dates.push(arrayObject)
                this.subBrandings.push(arrayObject as any as BrandingData)
            }
        }
        return this._dates
    }

    get showTimePicker() {
        return this.jsonData["showTimePicker"]
    }

    get times() {
        if (!this._times) {
            this._times = []
            for (const arrayElement of this.jsonData["times"]) {
                const arrayObject = new NotificationTemplateTimeImpl(arrayElement, this.lang)
                this.times.push(arrayObject)
                this.subBrandings.push(arrayObject as any as BrandingData)
            }
        }
        return this._times
    }

    get lblEntireDay() {
        return this.jsonData["lblEntireDay"][this.lang]
    }

    get defaultStartDate() {
        return this.jsonData["defaultStartDate"]
    }

    get defaultEndDate() {
        return this.jsonData["defaultEndDate"]
    }

    get titleEmptyCoupon() {
        return this.jsonData["titleEmptyCoupon"][this.lang]
    }

    get lblCouponsName() {
        return this.jsonData["lblCouponsName"][this.lang]
    }

    get hntCouponsName() {
        return this.jsonData["hntCouponsName"][this.lang]
    }

    get lblDescriptionTitle() {
        return this.jsonData["lblDescriptionTitle"][this.lang]
    }

    get hntDescriptionTitle() {
        return this.jsonData["hntDescriptionTitle"][this.lang]
    }

    get lblDescriptionLong() {
        return this.jsonData["lblDescriptionLong"][this.lang]
    }

    get hntDescriptionLong() {
        return this.jsonData["hntDescriptionLong"][this.lang]
    }
}

class NotificationTemplateDateImpl extends BrandingData implements NotificationTemplateDate {
    _id!: string;
    _date!: string;

    get id() {
        return this.jsonData["id"]
    }

    get date() {
        return this.jsonData["date"]
    }
}

class NotificationTemplateTimeImpl extends BrandingData implements NotificationTemplateTime {
    _id!: string;
    _start!: string;
    _end!: string;

    get id() {
        return this.jsonData["id"]
    }

    get start() {
        return this.jsonData["start"]
    }

    get end() {
        return this.jsonData["end"]
    }
}

class PrintPageConfigurationImpl extends BrandingData implements PrintPageConfiguration {
    _pageDescription!: LocalizedString;
    _sectionsOrder!: PrintSection[];
    _deadLine!: string;
    _lblCopyData!: LocalizedString;
    _sctLogoBackgroundImage!: LogoBackgroundImageSectionConfiguration;
    _sctCompanyName!: CompanyNameSectionConfiguration;
    _sctCompanySort!: CompanySortSectionConfiguration;
    _sctAddressPrintCatalogue!: AddressPrintCatalogueSectionConfiguration;
    _sctContactInformation!: ContactInformationSectionConfiguration;
    _sctCompanyDescription!: CompanyDescriptionSectionConfiguration;
    _sctAds!: AdsSectionConfiguration;

    get pageDescription() {
        return this.jsonData["pageDescription"][this.lang]
    }

    get sectionsOrder() {
        return this.jsonData["sectionsOrder"]
    }

    get deadLine() {
        return this.jsonData["deadLine"]
    }

    get lblCopyData() {
        return this.jsonData["lblCopyData"][this.lang]
    }

    get sctLogoBackgroundImage() {
        if (!this._sctLogoBackgroundImage) {
            this._sctLogoBackgroundImage = new LogoBackgroundImageSectionConfigurationImpl(this.jsonData["sctLogoBackgroundImage"], this.lang)
            this.subBrandings.push(this._sctLogoBackgroundImage as any as BrandingData)
        }
        return this._sctLogoBackgroundImage
    }

    get sctCompanyName() {
        if (!this._sctCompanyName) {
            this._sctCompanyName = new CompanyNameSectionConfigurationImpl(this.jsonData["sctCompanyName"], this.lang)
            this.subBrandings.push(this._sctCompanyName as any as BrandingData)
        }
        return this._sctCompanyName
    }

    get sctCompanySort() {
        if (!this._sctCompanySort) {
            this._sctCompanySort = new CompanySortSectionConfigurationImpl(this.jsonData["sctCompanySort"], this.lang)
            this.subBrandings.push(this._sctCompanySort as any as BrandingData)
        }
        return this._sctCompanySort
    }

    get sctAddressPrintCatalogue() {
        if (!this._sctAddressPrintCatalogue) {
            this._sctAddressPrintCatalogue = new AddressPrintCatalogueSectionConfigurationImpl(this.jsonData["sctAddressPrintCatalogue"], this.lang)
            this.subBrandings.push(this._sctAddressPrintCatalogue as any as BrandingData)
        }
        return this._sctAddressPrintCatalogue
    }

    get sctContactInformation() {
        if (!this._sctContactInformation) {
            this._sctContactInformation = new ContactInformationSectionConfigurationImpl(this.jsonData["sctContactInformation"], this.lang)
            this.subBrandings.push(this._sctContactInformation as any as BrandingData)
        }
        return this._sctContactInformation
    }

    get sctCompanyDescription() {
        if (!this._sctCompanyDescription) {
            this._sctCompanyDescription = new CompanyDescriptionSectionConfigurationImpl(this.jsonData["sctCompanyDescription"], this.lang)
            this.subBrandings.push(this._sctCompanyDescription as any as BrandingData)
        }
        return this._sctCompanyDescription
    }

    get sctAds() {
        if (!this._sctAds) {
            this._sctAds = new AdsSectionConfigurationImpl(this.jsonData["sctAds"], this.lang)
            this.subBrandings.push(this._sctAds as any as BrandingData)
        }
        return this._sctAds
    }
}

class LogoBackgroundImageSectionConfigurationImpl extends BrandingData implements LogoBackgroundImageSectionConfiguration {
    _lblSectionTitleLogoBackground!: LocalizedString;
    _lblCompanyLogoImageMessage!: LocalizedString;
    _hntTitle!: LocalizedString;
    _hntProp!: LocalizedString;
    _btnBrowseFiles!: LocalizedString;
    _btnDownloadFile!: LocalizedString;
    _btnDeleteImg!: LocalizedString;

    get lblSectionTitleLogoBackground() {
        return this.jsonData["lblSectionTitleLogoBackground"][this.lang]
    }

    get lblCompanyLogoImageMessage() {
        return this.jsonData["lblCompanyLogoImageMessage"][this.lang]
    }

    get hntTitle() {
        return this.jsonData["hntTitle"][this.lang]
    }

    get hntProp() {
        return this.jsonData["hntProp"][this.lang]
    }

    get btnBrowseFiles() {
        return this.jsonData["btnBrowseFiles"][this.lang]
    }

    get btnDownloadFile() {
        return this.jsonData["btnDownloadFile"][this.lang]
    }

    get btnDeleteImg() {
        return this.jsonData["btnDeleteImg"][this.lang]
    }
}

class CompanyNameSectionConfigurationImpl extends BrandingData implements CompanyNameSectionConfiguration {
    _lblSectionCompanyName!: LocalizedString;
    _lblCompanyName!: LocalizedString;
    _hntCompanyName!: LocalizedString;

    get lblSectionCompanyName() {
        return this.jsonData["lblSectionCompanyName"][this.lang]
    }

    get lblCompanyName() {
        return this.jsonData["lblCompanyName"][this.lang]
    }

    get hntCompanyName() {
        return this.jsonData["hntCompanyName"][this.lang]
    }
}

class CompanySortSectionConfigurationImpl extends BrandingData implements CompanySortSectionConfiguration {
    _lblSectionCompanySort!: LocalizedString;
    _lblCompanySort!: LocalizedString;

    get lblSectionCompanySort() {
        return this.jsonData["lblSectionCompanySort"][this.lang]
    }

    get lblCompanySort() {
        return this.jsonData["lblCompanySort"][this.lang]
    }
}

class AddressPrintCatalogueSectionConfigurationImpl extends BrandingData implements AddressPrintCatalogueSectionConfiguration {
    _lblSectionAddressInformation!: LocalizedString;
    _lblAddress1!: LocalizedString;
    _hntAddress1!: LocalizedString;
    _lblAddress2!: LocalizedString;
    _hntAddress2!: LocalizedString;
    _lblAddress3!: LocalizedString;
    _hntAddress3!: LocalizedString;
    _lblZipCode!: LocalizedString;
    _hntZipCode!: LocalizedString;
    _lblCity!: LocalizedString;
    _hntCity!: LocalizedString;
    _lblCountry!: LocalizedString;
    _hntCountry!: LocalizedString;

    get lblSectionAddressInformation() {
        return this.jsonData["lblSectionAddressInformation"][this.lang]
    }

    get lblAddress1() {
        return this.jsonData["lblAddress1"][this.lang]
    }

    get hntAddress1() {
        return this.jsonData["hntAddress1"][this.lang]
    }

    get lblAddress2() {
        return this.jsonData["lblAddress2"][this.lang]
    }

    get hntAddress2() {
        return this.jsonData["hntAddress2"][this.lang]
    }

    get lblAddress3() {
        return this.jsonData["lblAddress3"][this.lang]
    }

    get hntAddress3() {
        return this.jsonData["hntAddress3"][this.lang]
    }

    get lblZipCode() {
        return this.jsonData["lblZipCode"][this.lang]
    }

    get hntZipCode() {
        return this.jsonData["hntZipCode"][this.lang]
    }

    get lblCity() {
        return this.jsonData["lblCity"][this.lang]
    }

    get hntCity() {
        return this.jsonData["hntCity"][this.lang]
    }

    get lblCountry() {
        return this.jsonData["lblCountry"][this.lang]
    }

    get hntCountry() {
        return this.jsonData["hntCountry"][this.lang]
    }
}

class ContactInformationSectionConfigurationImpl extends BrandingData implements ContactInformationSectionConfiguration {
    _lblSectionContactInfo!: LocalizedString;
    _lblPhone!: LocalizedString;
    _hntPhone!: LocalizedString;
    _lblMobile!: LocalizedString;
    _hntMobile!: LocalizedString;
    _lblFax!: LocalizedString;
    _hntFax!: LocalizedString;
    _lblEmail!: LocalizedString;
    _hntEmail!: LocalizedString;
    _lblWebsite!: LocalizedString;
    _hntWebsite!: LocalizedString;

    get lblSectionContactInfo() {
        return this.jsonData["lblSectionContactInfo"][this.lang]
    }

    get lblPhone() {
        return this.jsonData["lblPhone"][this.lang]
    }

    get hntPhone() {
        return this.jsonData["hntPhone"][this.lang]
    }

    get lblMobile() {
        return this.jsonData["lblMobile"][this.lang]
    }

    get hntMobile() {
        return this.jsonData["hntMobile"][this.lang]
    }

    get lblFax() {
        return this.jsonData["lblFax"][this.lang]
    }

    get hntFax() {
        return this.jsonData["hntFax"][this.lang]
    }

    get lblEmail() {
        return this.jsonData["lblEmail"][this.lang]
    }

    get hntEmail() {
        return this.jsonData["hntEmail"][this.lang]
    }

    get lblWebsite() {
        return this.jsonData["lblWebsite"][this.lang]
    }

    get hntWebsite() {
        return this.jsonData["hntWebsite"][this.lang]
    }
}

class CompanyDescriptionSectionConfigurationImpl extends BrandingData implements CompanyDescriptionSectionConfiguration {
    _lblSectionCompanyDescription!: LocalizedString;
    _lblCompanyPortrait!: LocalizedString;
    _hntCompanyPortrait!: LocalizedString;

    get lblSectionCompanyDescription() {
        return this.jsonData["lblSectionCompanyDescription"][this.lang]
    }

    get lblCompanyPortrait() {
        return this.jsonData["lblCompanyPortrait"][this.lang]
    }

    get hntCompanyPortrait() {
        return this.jsonData["hntCompanyPortrait"][this.lang]
    }
}

class AdsSectionConfigurationImpl extends BrandingData implements AdsSectionConfiguration {
    _lblAdsSection!: LocalizedString;
    _lblAdsDecription!: LocalizedString;
    _hntDropzoneTitle!: LocalizedString;
    _hntDropzoneProp!: LocalizedString;
    _lblHeaderPosition!: LocalizedString;
    _lblHeaderPreview!: LocalizedString;
    _lblHeaderFile!: LocalizedString;

    get lblAdsSection() {
        return this.jsonData["lblAdsSection"][this.lang]
    }

    get lblAdsDecription() {
        return this.jsonData["lblAdsDecription"][this.lang]
    }

    get hntDropzoneTitle() {
        return this.jsonData["hntDropzoneTitle"][this.lang]
    }

    get hntDropzoneProp() {
        return this.jsonData["hntDropzoneProp"][this.lang]
    }

    get lblHeaderPosition() {
        return this.jsonData["lblHeaderPosition"][this.lang]
    }

    get lblHeaderPreview() {
        return this.jsonData["lblHeaderPreview"][this.lang]
    }

    get lblHeaderFile() {
        return this.jsonData["lblHeaderFile"][this.lang]
    }
}

class ValidationMessagesConfigurationImpl extends BrandingData implements ValidationMessagesConfiguration {
    _successMessageTitle!: LocalizedString;
    _successMessageText!: LocalizedString;
    _warningMessageTitle!: LocalizedString;
    _errorMessageTitle!: LocalizedString;
    _missingCompanyName!: LocalizedString;
    _missingCompanyLongDescription!: LocalizedString;
    _missingCompanyShortDescription!: LocalizedString;
    _missingCompanyAddress!: LocalizedString;
    _missingCompanyPostcode!: LocalizedString;
    _missingCompanyCity!: LocalizedString;
    _missingCompanyCountry!: LocalizedString;
    _missingCompanyPhone!: LocalizedString;
    _missingCompanyMobile!: LocalizedString;
    _missingCompanyFax!: LocalizedString;
    _invalidCompanyEmail!: LocalizedString;
    _missingCompanyEmail!: LocalizedString;
    _missingCompanyWebsite!: LocalizedString;
    _missingCompanySize!: LocalizedString;
    _missingCompanyIndustry!: LocalizedString;
    _missingCompanyFoundingYear!: LocalizedString;
    _missingLinkName!: LocalizedString;
    _missingLinkUrl!: LocalizedString;
    _missingProductName!: LocalizedString;
    _missingProductCollection!: LocalizedString;
    _invalidProductPublicationEndDate!: LocalizedString;
    _missingTrademarkName!: LocalizedString;
    _missingFirstName!: LocalizedString;
    _missingLastName!: LocalizedString;
    _missingEmail!: LocalizedString;
    _invalidEmail!: LocalizedString;
    _missingRole!: LocalizedString;
    _missingMediaTitle!: LocalizedString;
    _missingNewsTitle!: LocalizedString;
    _missingNewsDate!: LocalizedString;
    _missingNewsStoryMedias!: LocalizedString;
    _missingEventDateName!: LocalizedString;
    _missingEventDateLocation!: LocalizedString;
    _invalidEventDateTimeDifference!: LocalizedString;
    _invalidEventDateTimeFormat!: LocalizedString;
    _missingShowroomTitle!: LocalizedString;
    _missingCouponName!: LocalizedString;
    _missingCouponValidUrl!: LocalizedString;
    _missingValidStartDate!: LocalizedString;
    _missingValidEndDate!: LocalizedString;
    _missingJobTitle!: LocalizedString;
    _missingJobValidStartDate!: LocalizedString;
    _missingJobValidStartDisplaying!: LocalizedString;
    _missingPrintCompanyName!: LocalizedString;
    _missingPrintAddress!: LocalizedString;
    _missingPrintZipcode!: LocalizedString;
    _missingPrintCity!: LocalizedString;
    _missingPrintCountry!: LocalizedString;
    _missingDestinations!: LocalizedString;
    _missingDateTime!: LocalizedString;

    get successMessageTitle() {
        return this.jsonData["successMessageTitle"][this.lang]
    }

    get successMessageText() {
        return this.jsonData["successMessageText"][this.lang]
    }

    get warningMessageTitle() {
        return this.jsonData["warningMessageTitle"][this.lang]
    }

    get errorMessageTitle() {
        return this.jsonData["errorMessageTitle"][this.lang]
    }

    get missingCompanyName() {
        return this.jsonData["missingCompanyName"][this.lang]
    }

    get missingCompanyLongDescription() {
        return this.jsonData["missingCompanyLongDescription"][this.lang]
    }

    get missingCompanyShortDescription() {
        return this.jsonData["missingCompanyShortDescription"][this.lang]
    }

    get missingCompanyAddress() {
        return this.jsonData["missingCompanyAddress"][this.lang]
    }

    get missingCompanyPostcode() {
        return this.jsonData["missingCompanyPostcode"][this.lang]
    }

    get missingCompanyCity() {
        return this.jsonData["missingCompanyCity"][this.lang]
    }

    get missingCompanyCountry() {
        return this.jsonData["missingCompanyCountry"][this.lang]
    }

    get missingCompanyPhone() {
        return this.jsonData["missingCompanyPhone"][this.lang]
    }

    get missingCompanyMobile() {
        return this.jsonData["missingCompanyMobile"][this.lang]
    }

    get missingCompanyFax() {
        return this.jsonData["missingCompanyFax"][this.lang]
    }

    get invalidCompanyEmail() {
        return this.jsonData["invalidCompanyEmail"][this.lang]
    }

    get missingCompanyEmail() {
        return this.jsonData["missingCompanyEmail"][this.lang]
    }

    get missingCompanyWebsite() {
        return this.jsonData["missingCompanyWebsite"][this.lang]
    }

    get missingCompanySize() {
        return this.jsonData["missingCompanySize"][this.lang]
    }

    get missingCompanyIndustry() {
        return this.jsonData["missingCompanyIndustry"][this.lang]
    }

    get missingCompanyFoundingYear() {
        return this.jsonData["missingCompanyFoundingYear"][this.lang]
    }

    get missingLinkName() {
        return this.jsonData["missingLinkName"][this.lang]
    }

    get missingLinkUrl() {
        return this.jsonData["missingLinkUrl"][this.lang]
    }

    get missingProductName() {
        return this.jsonData["missingProductName"][this.lang]
    }

    get missingProductCollection() {
        return this.jsonData["missingProductCollection"][this.lang]
    }

    get invalidProductPublicationEndDate() {
        return this.jsonData["invalidProductPublicationEndDate"][this.lang]
    }

    get missingTrademarkName() {
        return this.jsonData["missingTrademarkName"][this.lang]
    }

    get missingFirstName() {
        return this.jsonData["missingFirstName"][this.lang]
    }

    get missingLastName() {
        return this.jsonData["missingLastName"][this.lang]
    }

    get missingEmail() {
        return this.jsonData["missingEmail"][this.lang]
    }

    get invalidEmail() {
        return this.jsonData["invalidEmail"][this.lang]
    }

    get missingRole() {
        return this.jsonData["missingRole"][this.lang]
    }

    get missingMediaTitle() {
        return this.jsonData["missingMediaTitle"][this.lang]
    }

    get missingNewsTitle() {
        return this.jsonData["missingNewsTitle"][this.lang]
    }

    get missingNewsDate() {
        return this.jsonData["missingNewsDate"][this.lang]
    }

    get missingNewsStoryMedias() {
        return this.jsonData["missingNewsStoryMedias"][this.lang]
    }

    get missingEventDateName() {
        return this.jsonData["missingEventDateName"][this.lang]
    }

    get missingEventDateLocation() {
        return this.jsonData["missingEventDateLocation"][this.lang]
    }

    get invalidEventDateTimeDifference() {
        return this.jsonData["invalidEventDateTimeDifference"][this.lang]
    }

    get invalidEventDateTimeFormat() {
        return this.jsonData["invalidEventDateTimeFormat"][this.lang]
    }

    get missingShowroomTitle() {
        return this.jsonData["missingShowroomTitle"][this.lang]
    }

    get missingCouponName() {
        return this.jsonData["missingCouponName"][this.lang]
    }

    get missingCouponValidUrl() {
        return this.jsonData["missingCouponValidUrl"][this.lang]
    }

    get missingValidStartDate() {
        return this.jsonData["missingValidStartDate"][this.lang]
    }

    get missingValidEndDate() {
        return this.jsonData["missingValidEndDate"][this.lang]
    }

    get missingJobTitle() {
        return this.jsonData["missingJobTitle"][this.lang]
    }

    get missingJobValidStartDate() {
        return this.jsonData["missingJobValidStartDate"][this.lang]
    }

    get missingJobValidStartDisplaying() {
        return this.jsonData["missingJobValidStartDisplaying"][this.lang]
    }

    get missingPrintCompanyName() {
        return this.jsonData["missingPrintCompanyName"][this.lang]
    }

    get missingPrintAddress() {
        return this.jsonData["missingPrintAddress"][this.lang]
    }

    get missingPrintZipcode() {
        return this.jsonData["missingPrintZipcode"][this.lang]
    }

    get missingPrintCity() {
        return this.jsonData["missingPrintCity"][this.lang]
    }

    get missingPrintCountry() {
        return this.jsonData["missingPrintCountry"][this.lang]
    }

    get missingDestinations() {
        return this.jsonData["missingDestinations"][this.lang]
    }

    get missingDateTime() {
        return this.jsonData["missingDateTime"][this.lang]
    }
}

class ErrorMessagesConfigurationImpl extends BrandingData implements ErrorMessagesConfiguration {
    _invalidFileType!: LocalizedString;

    get invalidFileType() {
        return this.jsonData["invalidFileType"][this.lang]
    }
}

class CategoriesCountableConfigurationImpl extends BrandingData implements CategoriesCountableConfiguration {
    _categoriesCountableCategories!: string[];
    _jobCountableCategories!: string[];
    _newsCountableCategories!: string[];
    _productCountableCategories!: string[];
    _trademarkCountableCategories!: string[];
    _warningMessage!: LocalizedString;

    get categoriesCountableCategories() {
        return this.jsonData["categoriesCountableCategories"]
    }

    get jobCountableCategories() {
        return this.jsonData["jobCountableCategories"]
    }

    get newsCountableCategories() {
        return this.jsonData["newsCountableCategories"]
    }

    get productCountableCategories() {
        return this.jsonData["productCountableCategories"]
    }

    get trademarkCountableCategories() {
        return this.jsonData["trademarkCountableCategories"]
    }

    get warningMessage() {
        return this.jsonData["warningMessage"][this.lang]
    }
}
declare let branding: Branding
let brandingImpl: any

export default function getBranding(): Branding {
    if (!brandingImpl)
        brandingImpl = new BrandingImpl(branding, "en")
    return brandingImpl
}

export function setBrandingLanguage(lang: string): void {
    ; (getBranding() as any).setLang(lang)
}
